/**
 * BlockCanvasEditor - Dedicated blocks-only canvas editor
 *
 * Focuses exclusively on strategy block editing with:
 * - BlockFlowCoordinator for block lifecycle
 * - BlockTemplatePanel for block templates
 * - BlockFormDialog for block editing
 * - Independent undo/redo history
 *
 * Props:
 * - projectId, strategyId: Required for BlockFlowCoordinator
 * - onBlockDoubleClick: Callback for transitioning to screens mode
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { BaseDialog } from '@modules/ui-components';
import { ReactFlowCanvas, ReactFlowCanvasProvider } from '@modules/reactFlow/components/shared/ReactFlowCanvas';
import { BlockTemplatePanel } from '@modules/reactFlow/components/block/BlockTemplatePanel';
import { TransitionEditor } from '@modules/reactFlow/components/shared/TransitionEditor';
import { EdgeDeleteConfirmation } from '@modules/reactFlow/components/shared/EdgeDeleteConfirmation';
import { isKeyboardCapturedByOverlay } from '@modules/reactFlow/utils/keyboardGuard';
import { TopControlPanel } from '@modules/reactFlow/components/shared/TopControlPanel';
import type { StrategyBlock } from '@segmently/schema-models/strategy';

/** Legacy registry payloads may still carry `blockName` (superseded by `name`). */
type MaybeLegacyBlock = StrategyBlock & { blockName?: string };
import type { Edge } from '@xyflow/react';
import { blockRegistry } from '@modules/reactFlow/services/block/BlockRegistry';
import {
  BlockFlowCoordinator,
  EXAMPLE_SECONDARY_TEMPLATE_ID_PREFIX,
} from '@modules/reactFlow/services/block/BlockFlowCoordinator';
import { BlockFormDialog } from '@modules/strategy-blocks/components/BlockFormDialog';
import { EditEntityCommand } from '@modules/reactFlow/commands/EditEntityCommand';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import { useCanvasEditorState } from './hooks/useCanvasEditorState';
import { useCanvasEventHandlers } from './hooks/useCanvasEventHandlers';
import type { EventHandlerContext, EdgeDialogState } from './hooks/useCanvasEventHandlers';
import { OnboardingCanvasEditorV2 } from '../onboarding/OnboardingCanvasEditorV2';
import { StrategyV2ScreenFlowCoordinator } from '@modules/reactFlow/services/strategy/StrategyV2ScreenFlowCoordinator';
import { CanvasTasksPanel } from './CanvasTasksPanel';
import { MotivationScorecardPanel } from './MotivationScorecardPanel';
import { StrategyHealthBadge, StrategyHealthPanel } from './StrategyHealthPanel';
import { ApplyThemeGroupsDialog, type ApplyThemeGroupsConfirmPayload } from '@modules/themes/components/ApplyThemeGroupsDialog';
import {
  StrategyThemeSelectionDialog,
  type StrategyThemeSelectionAction,
  type StrategyThemeSelectionOption,
} from '@modules/themes/components/StrategyThemeSelectionDialog';
import { useStrategyThemeSettings } from '@modules/themes/hooks/useStrategyThemeSettings';
import type { ThemeV2 } from '@modules/themes/types/themesV2.types';
import { cloneOnboardingThemeSettings, onboardingSettingsV2ToThemeV2, themeV2ToOnboardingSettings } from '@modules/themes/utils';
import { ALL_THEME_APPLY_GROUPS, type ThemeApplyGroup } from '@modules/themes/mappers/themeApplyGroups';
import { preserveThemeApplyScreenContent } from '@modules/themes/mappers/preserveThemeApplyScreenContent';
import { StrategyExportService } from '@modules/strategy-blocks/services/StrategyExportService';
import { StrategyImportDialog } from '@modules/strategy-blocks/components/StrategyImportDialog';
import { StrategyCanvasActions } from '@modules/strategy-blocks/components/StrategyCanvasActions';
import { InsightImpactDialog } from '@modules/strategy-blocks/components/InsightImpactDialog';
import { StrategyDivergenceChip } from '@modules/strategy-blocks/components/StrategyDivergenceChip';
import { StrategyVersionSwitcher } from '@modules/strategy-blocks/components/StrategyVersionSwitcher';
import { useStrategyVersionActions } from '@modules/strategy-blocks/hooks/useStrategyVersionActions';
import { StrategyStalenessBadge } from '@modules/strategy-blocks/components/StrategyStalenessBadge';
import { StrategyForkDialog } from '@modules/strategy-blocks/components/StrategyForkDialog';
import { StrategyLineageChip } from '@modules/strategy-blocks/components/StrategyLineageChip';
import { StrategyStalenessService } from '@modules/strategy-blocks/services/StrategyStalenessService';
import { StrategyForkService } from '@modules/strategy-blocks/services/StrategyForkService';
import { StrategyCompileDialog, type StrategyCompilePayload } from '@modules/strategy-blocks/components/StrategyCompileDialog';
import { StrategyLiftDialog, type StrategyLiftPayload } from '@modules/strategy-blocks/components/StrategyLiftDialog';
import { StrategySyncConfirmDialog } from '@modules/strategy-blocks/components/StrategySyncConfirmDialog';
import {
  resolveCliStrategyHealthFindingTarget,
  type CliStrategyHealthFinding,
} from '@modules/shared-types';
import type {
  CliStrategyHealthReport,
  CliStrategyPushData,
  CliStrategyStaleness,
  CliStrategySyncResolutions,
} from '@modules/shared-types';
import { BlockScreenGenerationDialog, type BlockScreenGenerationPayload } from '@modules/strategy-blocks/components/BlockScreenGenerationDialog';
import { CloneBlockPopover, type CloneBlockPopoverSubmit, type CloneBlockVariantsSubmit } from '@modules/strategy-blocks/components/CloneBlockPopover';
import { SaveBlockAsExampleDialog } from '@modules/strategy-blocks/components/SaveBlockAsExampleDialog';
import { InsertBuildingBlockDialog } from '@modules/strategy-blocks/components/InsertBuildingBlockDialog';
import { LibraryAdaptationInspector } from '@modules/strategy-blocks/components/LibraryAdaptationInspector';
import { readLibraryAdaptationState } from '@modules/strategy-blocks/utils/libraryAdaptationState';
import { resolveHeadVariablePackage } from '@modules/strategy-blocks/utils/headVariablePackage';
import { BlockLibraryBrowserDialog } from '@modules/strategy-blocks/components/BlockLibraryBrowserDialog';
import { ExampleDetailsDialog } from '@modules/strategy-blocks/components/ExampleDetailsView';
import { importSystemExamples } from '@modules/strategy-blocks/services/blockLibraryImport';
import { AdaptingBlocksProvider } from '@modules/reactFlow/contexts/AdaptingBlocksContext';
import { useFollowStrategyGeneration } from '@modules/reactFlow/hooks/useFollowStrategyGeneration';
import { computeContractFit } from '@modules/shared-types';
import type { BlockLibraryExampleDoc, ContractFitEntry } from '@modules/shared-types';
import type { ExampleInsertGateDecision } from '@modules/reactFlow/services/block/BlockFlowCoordinator';

/** Display meta for the Insert dialog rows (from the envelope contract). */
function exampleVariableMeta(example: BlockLibraryExampleDoc): Record<string, { name?: string; type?: string }> {
  const contract = (example.blueprint.contract ?? {}) as Record<string, unknown>;
  const meta: Record<string, { name?: string; type?: string }> = {};
  for (const list of [contract.inputVariables, contract.outputAssignment]) {
    if (!Array.isArray(list)) continue;
    for (const definition of list) {
      if (definition && typeof definition === 'object' && typeof (definition as { id?: unknown }).id === 'string') {
        const def = definition as { id: string; name?: string; type?: string };
        meta[def.id] = { name: def.name, type: def.type };
      }
    }
  }
  return meta;
}
import type { ImportResult } from '@modules/strategy-blocks/services/StrategyImportService';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { StrategyStepNodeService } from '@modules/firebase-core/services/StrategyStepNodeService';
import { ProjectThemeService } from '@modules/firebase-core/services/ProjectThemeService';
import { ThemeV2Service } from '@modules/firebase-core/services/ThemeV2Service';
import type { FunnelVariable, StepNode } from '@segmently/schema-models/v2';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { taskFirestoreService, TERMINAL_STATUSES } from '@modules/background-tasks';
import { useBlockSubtypes } from '@modules/strategy-blocks/hooks/useBlockSubtypes';
import { useBlockLibraryExamples } from '@modules/strategy-blocks/hooks/useBlockLibraryExamples';
import { useSystemBlockLibraryExamples } from '@modules/strategy-blocks/hooks/useSystemBlockLibraryExamples';
import { BlockSubtypeManagerDialog } from '@modules/strategy-blocks/components/BlockSubtypeManagerDialog';
import { StrategyOrchestrator } from '@modules/strategy-blocks/services/StrategyOrchestrator';
import { SourceDataHydrator } from '@modules/strategy-blocks/services/SourceDataHydrator';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';
import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { RatingService } from '@modules/firebase-core/services/RatingService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';
import { audienceConverter } from '@modules/firebase-core/converters/audienceConverter';
import { taskConverter } from '@modules/firebase-core/converters/taskConverter';
import { studyCaseConverter } from '@modules/firebase-core/converters/studyCaseConverter';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';

const DEFAULT_SCREEN_GENERATION_MAX_TOKENS = 100000;

type StrategyApplyScreenRuntimeFields = {
  __strategyBlockId?: string;
  __strategyBlockName?: string;
  __strategyScreenId?: string;
};

function stripStrategyApplyRuntimeFields<T extends Record<string, unknown>>(screen: T): T {
  const {
    __strategyBlockId: _blockId,
    __strategyBlockName: _blockName,
    __strategyScreenId: _screenId,
    ...clean
  } = screen;
  return clean as T;
}

export interface BlockCanvasEditorProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  strategyId: string;
  strategyName?: string;
  initialScreensBlockId?: string | null;
  initialScreensScreenId?: string | null;
  onOpenBlockScreens?: (blockId: string) => void;
  onScreensModalClose?: () => void;
  onOpenBlockScreen?: (blockId: string, screenId: string) => void;
  onScreenEditorClose?: () => void;
}

export const BlockCanvasEditor: React.FC<BlockCanvasEditorProps> = ({
  open,
  onClose,
  projectId,
  strategyId,
  strategyName: initialStrategyName,
  initialScreensBlockId,
  initialScreensScreenId,
  onOpenBlockScreens,
  onScreensModalClose,
  onOpenBlockScreen,
  onScreenEditorClose,
}) => {
  // State management via shared hook
  const {
    canvasHandle,
    handleCanvasReady,
    selectedScreenIds,
    setSelectedScreenIds,
    launchScreenId,
    setLaunchScreenId,
    isLoading,
    setIsLoading,
    isSyncing,
    setIsSyncing,
    syncOperation,
    setSyncOperation,
    commandManager,
    confirmDialog,
    setConfirmDialog,
    showConfirmation,
    settersRef,
  } = useCanvasEditorState('blocks');

  // Task tracking is handled by the Zustand store via collection listener (background-tasks module)

  // Restores replace head metadata as well as blocks. Use the refreshed document
  // for every title/prompt/dialog instead of the parent's initial display hint.
  const [strategyDoc, setStrategyDoc] = useState<Record<string, any> | null>(null);
  const strategyName = strategyDoc?.name ?? initialStrategyName;

  // Strategy theme — for editing via dialog
  const { themeSettings: strategyThemeData, update: updateStrategyTheme, refresh: refreshStrategyTheme } = useStrategyThemeSettings({
    projectId,
    strategyId,
  });
  const strategyService = useMemo(() => new StrategyService(projectId), [projectId]);
  const blockSubtypeCatalog = useBlockSubtypes(projectId);
  // Block Examples Library: live palette EXAMPLES group.
  const blockLibraryExamples = useBlockLibraryExamples(projectId);
  // System tier (root catalogue) — feeds the library browser + system inserts.
  const systemBlockLibrary = useSystemBlockLibraryExamples();
  const [libraryBrowserOpen, setLibraryBrowserOpen] = useState(false);
  const blockSubtypesBySlug = useMemo(() => Object.fromEntries(
    blockSubtypeCatalog.subtypes.map((item) => [item.slug, { name: item.name, enabled: item.enabled }]),
  ), [blockSubtypeCatalog.subtypes]);
  const projectThemeService = useMemo(() => new ProjectThemeService(), []);
  const globalThemeService = useMemo(() => new ThemeV2Service(), []);
  const [themeReplaceOpen, setThemeReplaceOpen] = useState(false);
  const [themeReplaceLoading, setThemeReplaceLoading] = useState(false);
  const [themeReplaceError, setThemeReplaceError] = useState<string | null>(null);
  const [themeReplaceOptions, setThemeReplaceOptions] = useState<StrategyThemeSelectionOption[]>([]);
  const [selectedThemeReplaceOption, setSelectedThemeReplaceOption] = useState<StrategyThemeSelectionOption | null>(null);
  const [applyThemeDialog, setApplyThemeDialog] = useState<{
    theme: ThemeV2;
    screens: Array<StepNode & StrategyApplyScreenRuntimeFields>;
    sourceLabel: string;
    scopeLabel: string;
    initialGroups?: ThemeApplyGroup[];
  } | null>(null);

  const loadStrategyThemeReplaceOptions = useCallback(async () => {
    const [projectThemes, globalThemes] = await Promise.all([
      projectThemeService.list(projectId),
      globalThemeService.list({ limit: 100 }),
    ]);

    const projectOptions: StrategyThemeSelectionOption[] = projectThemes.map(theme => ({
      id: theme.id,
      name: theme.name || theme.id,
      source: 'project',
      isActive: theme.isActive,
      settings: cloneOnboardingThemeSettings(theme, {
        sourceThemeId: theme.sourceThemeId || theme.id,
      }),
    }));

    const globalOptions: StrategyThemeSelectionOption[] = globalThemes.map(theme => ({
      id: theme.id,
      name: theme.name || theme.id,
      source: 'global',
      settings: themeV2ToOnboardingSettings(theme, {
        sourceThemeId: theme.id,
      }),
    }));

    return [...projectOptions, ...globalOptions];
  }, [globalThemeService, projectId, projectThemeService]);

  const loadAllStrategyScreensForThemeApply = useCallback(async () => {
    const targets = await strategyService.listAllStepNodeScreens(strategyId);
    return targets.map(({ blockId, blockName, screenId, screen }) => ({
      ...(screen as StepNode),
      __strategyBlockId: blockId,
      __strategyBlockName: blockName,
      __strategyScreenId: screenId,
    }));
  }, [strategyId, strategyService]);

  const handleOpenReplaceTheme = useCallback(async () => {
    setThemeReplaceOpen(true);
    setThemeReplaceError(null);
    setThemeReplaceOptions([]);
    setSelectedThemeReplaceOption(null);
    setThemeReplaceLoading(true);
    try {
      const options = await loadStrategyThemeReplaceOptions();
      setThemeReplaceOptions(options);
      setSelectedThemeReplaceOption(options.find(option => option.source === 'project' && option.isActive) ?? options[0] ?? null);
    } catch (error) {
      console.error('[BlockCanvasEditor] Failed to load replacement themes:', error);
      setThemeReplaceError((error as Error).message);
    } finally {
      setThemeReplaceLoading(false);
    }
  }, [loadStrategyThemeReplaceOptions]);

  const handleApplyCurrentStrategyThemeToAllScreens = useCallback(async () => {
    if (!strategyThemeData) return;
    setThemeReplaceError(null);
    setThemeReplaceLoading(true);
    try {
      const screens = await loadAllStrategyScreensForThemeApply();
      if (screens.length === 0) {
        setThemeReplaceError('This strategy has no screens to update.');
        alert('This strategy has no screens to update.');
        return;
      }
      setApplyThemeDialog({
        theme: onboardingSettingsV2ToThemeV2(strategyThemeData, {
          id: 'strategy-theme-settings',
          name: strategyName || 'Strategy theme',
        }),
        screens,
        sourceLabel: 'Current strategy theme',
        scopeLabel: `All strategy screens (${screens.length})`,
        initialGroups: [...ALL_THEME_APPLY_GROUPS],
      });
    } catch (error) {
      console.error('[BlockCanvasEditor] Failed to prepare strategy theme apply:', error);
      setThemeReplaceError((error as Error).message);
      alert(`Theme apply failed: ${(error as Error).message}`);
    } finally {
      setThemeReplaceLoading(false);
    }
  }, [loadAllStrategyScreensForThemeApply, strategyName, strategyThemeData]);

  const handleConfirmReplaceTheme = useCallback(async (action: StrategyThemeSelectionAction) => {
    if (!selectedThemeReplaceOption) return;

    setThemeReplaceLoading(true);
    setThemeReplaceError(null);
    try {
      const settings = cloneOnboardingThemeSettings(selectedThemeReplaceOption.settings, {
        sourceThemeId: selectedThemeReplaceOption.source === 'global'
          ? selectedThemeReplaceOption.id
          : selectedThemeReplaceOption.settings.sourceThemeId || selectedThemeReplaceOption.id,
      });
      await updateStrategyTheme(settings);

      if (action === 'add-and-apply') {
        const screens = await loadAllStrategyScreensForThemeApply();
        if (screens.length === 0) {
          setThemeReplaceError('Theme was updated, but this strategy has no screens to update.');
          alert('Theme was updated, but this strategy has no screens to update.');
          return;
        }
        setApplyThemeDialog({
          theme: onboardingSettingsV2ToThemeV2(settings, {
            id: selectedThemeReplaceOption.id,
            name: selectedThemeReplaceOption.name,
          }),
          screens,
          sourceLabel: selectedThemeReplaceOption.source === 'project'
            ? `Project theme: ${selectedThemeReplaceOption.name}`
            : `Global theme: ${selectedThemeReplaceOption.name}`,
          scopeLabel: `All strategy screens (${screens.length})`,
          initialGroups: [...ALL_THEME_APPLY_GROUPS],
        });
        setThemeReplaceOpen(false);
      } else {
        setThemeReplaceOpen(false);
      }
    } catch (error) {
      console.error('[BlockCanvasEditor] Failed to replace strategy theme:', error);
      setThemeReplaceError((error as Error).message);
    } finally {
      setThemeReplaceLoading(false);
    }
  }, [
    loadAllStrategyScreensForThemeApply,
    selectedThemeReplaceOption,
    updateStrategyTheme,
  ]);

  const persistAppliedStrategyThemeScreens = useCallback(async (
    payload: ApplyThemeGroupsConfirmPayload<StepNode & StrategyApplyScreenRuntimeFields>
  ) => {
    setIsSyncing(true);
    setSyncOperation('Applying strategy theme');
    try {
      if (payload.themeWasEdited && payload.saveTheme) {
        await updateStrategyTheme(themeV2ToOnboardingSettings(payload.theme, {
          sourceThemeId: strategyThemeData?.sourceThemeId,
        }));
      }

      for (const change of payload.changes) {
        const beforeRuntime = change.before as StepNode & StrategyApplyScreenRuntimeFields;
        const blockId = beforeRuntime.__strategyBlockId;
        const screenId = beforeRuntime.__strategyScreenId || (change.before as any).id || (change.before as any).entityId;
        if (!blockId || !screenId) continue;

        const preservedAfter = preserveThemeApplyScreenContent(
          change.before as unknown as StepNode,
          change.after as unknown as StepNode,
        );
        await strategyService.replaceStepNodeScreen(
          strategyId,
          blockId,
          screenId,
          stripStrategyApplyRuntimeFields(preservedAfter as unknown as Record<string, unknown>) as unknown as StepNode,
        );
      }
      setApplyThemeDialog(null);
      alert(`Applied ${payload.changes.length} screen updates. Skipped ${payload.skippedNoop.length + payload.optedOut.length}. Unsupported ${payload.unsupported.length}.`);
    } finally {
      setIsSyncing(false);
      setSyncOperation('');
    }
  }, [
    setIsSyncing,
    setSyncOperation,
    strategyId,
    strategyService,
    strategyThemeData?.sourceThemeId,
    updateStrategyTheme,
  ]);

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importExporting, setImportExporting] = useState(false);
  const [compileDialogOpen, setCompileDialogOpen] = useState(false);
  const [compileLoading, setCompileLoading] = useState(false);
  const [liftDialogOpen, setLiftDialogOpen] = useState(false);
  const [liftLoading, setLiftLoading] = useState(false);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [pushDryRun, setPushDryRun] = useState<CliStrategyPushData | null>(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [syncNotice, setSyncNotice] = useState<{ message: string; severity: 'success' | 'info' | 'error' } | null>(null);
  // C7-2: "Clone Block…" popover target (anchored at the node's DOM rect).
  const [cloneBlockTarget, setCloneBlockTarget] = useState<{
    entityId: string;
    blockId: string;
    name: string;
    anchorPosition: { top: number; left: number };
  } | null>(null);
  const [cloneBusy, setCloneBusy] = useState(false);
  // Block Examples Library: "Save as Example…" dialog target.
  const [saveAsExampleTarget, setSaveAsExampleTarget] = useState<{
    blockId: string;
    name: string;
  } | null>(null);
  // Block Examples Library: Insert dialog (opened by the coordinator fit-gate
  // for EVERY insert — operator decision 2026-08-05: the adaptation model is
  // chosen at the launch surface; clean fits render the one-click variant).
  const [insertExampleDialog, setInsertExampleDialog] = useState<{
    /** null when the example doc hasn't synced locally (server auto-resolves). */
    example: BlockLibraryExampleDoc | null;
    exampleName: string;
    fit: ContractFitEntry[];
    resolve: (decision: ExampleInsertGateDecision | null) => void;
  } | null>(null);
  // From-library roundtrip pending (coordinator listener) — snackbar with a
  // spinner; the roundtrip otherwise reads as a dead click.
  const [exampleInsertPending, setExampleInsertPending] = useState<{
    exampleId: string;
    source: 'project' | 'system';
  } | null>(null);
  // Example DETAILS dialog (palette ⓘ) — screens + instructions before insert.
  const [exampleDetails, setExampleDetails] = useState<{
    example: BlockLibraryExampleDoc;
    source: 'project' | 'system';
  } | null>(null);
  const [screenGenerationBlockId, setScreenGenerationBlockId] = useState<string | null>(null);
  const [screenGenerationLoading, setScreenGenerationLoading] = useState(false);

  // Coordinator state
  const [coordinator, setCoordinator] = useState<BlockFlowCoordinator | null>(null);
  const coordinatorRef = useRef<BlockFlowCoordinator | null>(null);
  const blockTaskSubscriptionsRef = useRef<Record<string, () => void>>({});

  useEffect(() => {
    coordinatorRef.current = coordinator;
  }, [coordinator]);

  useEffect(() => {
    if (open) return;
    Object.values(blockTaskSubscriptionsRef.current).forEach((unsubscribe) => unsubscribe());
    blockTaskSubscriptionsRef.current = {};
  }, [open]);

  useEffect(() => () => {
    Object.values(blockTaskSubscriptionsRef.current).forEach((unsubscribe) => unsubscribe());
    blockTaskSubscriptionsRef.current = {};
  }, []);

  const subscribeToBlockTaskCompletion = useCallback((taskId: string, blockId: string) => {
    if (!projectId || !taskId || !blockId || blockTaskSubscriptionsRef.current[taskId]) return;

    const unsubscribe = taskFirestoreService.subscribe(
      projectId,
      taskId,
      (task) => {
        if (!task || !TERMINAL_STATUSES.includes(task.status)) return;

        const cleanup = blockTaskSubscriptionsRef.current[taskId];
        if (cleanup) {
          cleanup();
          delete blockTaskSubscriptionsRef.current[taskId];
        }

        if (task.status === 'completed') {
          void coordinatorRef.current?.refreshBlockFromFirebase(blockId).catch((error) => {
            console.error('[BlockCanvasEditor] Failed to refresh block after task completion:', error);
          });
        }
      },
      (error) => {
        console.error('[BlockCanvasEditor] Block generation task listener error:', error);
      },
    );

    blockTaskSubscriptionsRef.current[taskId] = unsubscribe;
  }, [projectId]);

  const handleExportStrategy = useCallback(async () => {
    setImportExporting(true);
    try {
      const exportService = new StrategyExportService();
      const envelope = await exportService.exportStrategyPayload(projectId, strategyId);
      const name = strategyName?.replace(/\s+/g, '_') || strategyId;
      StrategyExportService.download(envelope, `strategy_${name}_${Date.now()}.json`);
    } catch (err) {
      console.error('[BlockCanvasEditor] Export failed:', err);
      alert(`Export failed: ${(err as Error).message}`);
    } finally {
      setImportExporting(false);
    }
  }, [projectId, strategyId, strategyName]);

  const handleImported = useCallback((result: ImportResult) => {
    console.log('[BlockCanvasEditor] Import complete:', result);
    // Reload canvas to show imported blocks
    window.location.reload();
  }, []);

  const hasCurrentStrategyTheme = useMemo(() => Boolean(
    strategyThemeData?.colors
      && strategyThemeData.fonts
      && strategyThemeData.optionsListSettings
      && strategyThemeData.background
      && strategyThemeData.headerBar
      && strategyThemeData.actionBar,
  ), [strategyThemeData]);

  const selectedScreenGenerationBlock = useMemo(() => {
    if (!screenGenerationBlockId) return null;
    return blockRegistry.get(screenGenerationBlockId) || null;
  }, [screenGenerationBlockId]);

  const handleCloseScreenGenerationDialog = useCallback(() => {
    if (screenGenerationLoading) return;
    setScreenGenerationBlockId(null);
  }, [screenGenerationLoading]);


  const handleGenerateBlockScreens = useCallback(async (payload: BlockScreenGenerationPayload) => {
    const block = selectedScreenGenerationBlock;
    if (!block?.id) return;

    setScreenGenerationLoading(true);
    try {
      console.log('[BlockCanvasEditor] Generating screens for block:', block.id, block.name);

      const agentClient = new AgentApiClient();

      const allBlocks = blockRegistry.getAll();
      const fullStrategySequence = allBlocks.map((b: any) => ({
        entityId: b.entityId || b.id,
        name: b.name || '',
        blockType: b.blockType || 'custom',
        description: b.description || '',
        screensCount: 0,
      }));

      const strategyService = new StrategyService(projectId);
      const strategy = await strategyService.get(strategyId);
      const strategyVariables = (strategy as any)?.variables;
      const screenService = new StrategyStepNodeService(projectId, strategyId, block.id);
      const existingScreens = await screenService.listScreens();
      const blockDescription = payload.blockDescription.trim();

      const { getAuth } = await import('firebase/auth');
      const userId = getAuth().currentUser?.uid || 'unknown';
      const prompt = [
        'Generate screens for this block based on its description and contract.',
        blockDescription
          ? `Block description:\n${blockDescription}`
          : null,
        payload.description
          ? `Additional user description:\n${payload.description}`
          : null,
      ].filter(Boolean).join('\n\n');

      // Grounding redesign: the server resolves sourceData/currentBlock from
      // the strategy snapshot (contextOwnedByServer) — only the description
      // edit is persisted; no picker data exists to write or send anymore.
      const descriptionUpdate = { description: blockDescription };
      const blockService = new StrategyBlockService(projectId, strategyId);
      await blockService.update(block.id, descriptionUpdate as any);
      if (block.entityId) {
        blockRegistry.update(block.entityId, descriptionUpdate as Partial<StrategyBlock>);
      }

      const result = await agentClient.updateBlock({
        projectId,
        strategyId,
        blockId: block.id,
        blockType: (block.blockType as string) || 'custom',
        prompt,
        userId,
        currentBlock: {
          ...(block as unknown as Record<string, unknown>),
          ...descriptionUpdate,
          screens: existingScreens,
        },
        blockContract: (block as any).blockContract,
        strategyVariables,
        fullStrategySequence,
        model: payload.model,
        maxTokens: DEFAULT_SCREEN_GENERATION_MAX_TOKENS,
        // C7-3: explicit operator override — honored server-side with
        // sourceDataOrigin 'operator-override' (absent on untouched dialogs).
        ...(payload.sourceDataOverride ? { sourceDataOverride: payload.sourceDataOverride } : {}),
        // HB-5: the thesis narrowing for THIS run. Theses are loaded from the
        // pinned analysis, never from sourceData, so they travel on the
        // selection channel and outrank the head's frozen narrowing here only.
        ...(payload.insightSelection ? { insightSelection: payload.insightSelection } : {}),
      });

      if (result.success) {
        console.log('[BlockCanvasEditor] Screen generation started for block:', block.id, 'taskId:', result.taskId);
        if (result.taskId) {
          subscribeToBlockTaskCompletion(result.taskId, block.id);
        }
        setScreenGenerationBlockId(null);
      } else {
        console.error('[BlockCanvasEditor] Screen generation failed:', result.error);
        alert(`Screen generation failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[BlockCanvasEditor] Error triggering screen generation:', err);
      alert(`Screen generation failed: ${(err as Error)?.message || 'Unknown error'}`);
    } finally {
      setScreenGenerationLoading(false);
    }
  }, [projectId, selectedScreenGenerationBlock, strategyId, subscribeToBlockTaskCompletion]);

  const handleOpenCompileDialog = useCallback(() => {
    setCompileDialogOpen(true);
  }, []);

  const handleCompilePreview = useCallback(async (payload: StrategyCompilePayload) => {
    const api = new AgentApiClient();
    const response = await api.compileStrategyToOnboarding({
      projectId,
      strategyId,
      ...payload,
      dryRun: true,
    });

    if (!response.success) {
      return { error: response.error || 'Compile preview failed' };
    }

    return {
      plan: response.plan,
      warnings: response.warnings,
      screenCount: response.screenCount,
      blockCount: response.blockCount,
      healthcheck: response.healthcheck,
      strategyHealthReport: response.strategyHealthReport,
    };
  }, [projectId, strategyId]);

  const handleLiftPreview = useCallback(async (payload: StrategyLiftPayload) => {
    const api = new AgentApiClient();
    const response = await api.liftOnboardingToStrategy({
      projectId,
      ...payload,
      dryRun: true,
    });

    if (!response.success) {
      return { error: response.error || 'Lift preview failed' };
    }

    return {
      blockCount: response.blockCount,
      screenCount: response.screenCount,
      blocks: response.blocks,
      looseScreens: response.looseScreens,
      foreignTagIgnored: response.foreignTagIgnored,
      variableDiff: response.variableDiff,
      warnings: response.warnings,
    };
  }, [projectId]);

  const handleLiftFromOnboarding = useCallback(async (payload: StrategyLiftPayload) => {
    setLiftLoading(true);
    try {
      const api = new AgentApiClient();
      const response = await api.liftOnboardingToStrategy({
        projectId,
        funnelId: payload.funnelId,
        versionId: payload.versionId,
        targetStrategyId: payload.targetStrategyId,
        name: payload.name,
        replaceExisting: payload.replaceExisting,
        resolutions: payload.resolutions,
      });

      if (!response.success) {
        throw new Error(response.error || 'Lift failed');
      }

      setLiftDialogOpen(false);
      setSyncNotice({
        message: `Strategy lifted with ${response.blockCount ?? 0} blocks and ${response.screenCount ?? 0} screens.`,
        severity: 'success',
      });

      const liftedStrategyId = response.strategyId || payload.targetStrategyId;
      if (!liftedStrategyId || liftedStrategyId === strategyId) {
        // Same-strategy lift: refresh blocks in place instead of a full page reload.
        if (coordinator) {
          await coordinator.loadBlocksFromFirebase();
        } else {
          window.location.reload();
        }
      } else {
        window.open(`/project/${projectId}/strategy/${liftedStrategyId}`, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('[BlockCanvasEditor] Lift onboarding to strategy failed:', err);
      setSyncNotice({ message: `Lift failed: ${(err as Error).message}`, severity: 'error' });
    } finally {
      setLiftLoading(false);
    }
  }, [projectId, strategyId, coordinator]);

  const handleOpenPushDialog = useCallback(async () => {
    setPushDialogOpen(true);
    setPushDryRun(null);
    setPushError(null);
    setPushLoading(true);
    try {
      const api = new AgentApiClient();
      const response = await api.pushStrategyToOnboarding({ projectId, strategyId, dryRun: true });
      if (!response.success) {
        throw new Error(response.error || 'Push dry-run failed');
      }
      setPushDryRun(response as unknown as CliStrategyPushData);
    } catch (err) {
      console.error('[BlockCanvasEditor] Push dry-run failed:', err);
      setPushError((err as Error).message);
    } finally {
      setPushLoading(false);
    }
  }, [projectId, strategyId]);

  const handleConfirmPush = useCallback(async (resolutions: CliStrategySyncResolutions) => {
    setPushLoading(true);
    setPushError(null);
    try {
      const api = new AgentApiClient();
      const response = await api.pushStrategyToOnboarding({ projectId, strategyId, resolutions });
      if (!response.success) {
        throw new Error(response.error || 'Push failed');
      }
      setPushDialogOpen(false);
      setSyncNotice({
        message: `Pushed strategy to onboarding: ${response.created ?? 0} created, ${response.replaced ?? 0} replaced, ${response.preserved ?? 0} preserved, ${response.deleted ?? 0} deleted.`,
        severity: 'success',
      });
    } catch (err) {
      console.error('[BlockCanvasEditor] Push to onboarding failed:', err);
      setPushError((err as Error).message);
    } finally {
      setPushLoading(false);
    }
  }, [projectId, strategyId]);

  // Dialog states
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [showEdgeDeleteConfirm, setShowEdgeDeleteConfirm] = useState(false);
  const [edgesToDelete, setEdgesToDelete] = useState<string[]>([]);
  const [libraryInspectorRevision, setLibraryInspectorRevision] = useState(0);
  const [blockFormOpen, setBlockFormOpen] = useState(false);
  const [selectedBlockForEdit, setSelectedBlockForEdit] = useState<StrategyBlock | null>(null);

  // Screens modal state (for double-click on blocks)
  const [screensModalOpen, setScreensModalOpen] = useState(false);
  const [selectedBlockIdForScreens, setSelectedBlockIdForScreens] = useState<string>('');
  const [selectedBlockNameForScreens, setSelectedBlockNameForScreens] = useState<string>('');
  const [screensModalSource, setScreensModalSource] = useState<'local' | 'url' | null>(null);

  // Strategy variables — loaded once, passed to screen canvas for condition editor
  const [strategyVariables, setStrategyVariables] = useState<Record<string, FunnelVariable>>({});
  // Advisory contract-fit line per palette example card (FP-7 honest states).
  const exampleFitSummaries = useMemo(() => {
    const summaries: Record<string, { mapped: number; created: number; needsAdapt: number }> = {};
    for (const example of blockLibraryExamples.examples) {
      try {
        const fit = computeContractFit(example, strategyVariables as Record<string, unknown>);
        summaries[example.id] = {
          mapped: fit.filter((entry) => entry.resolution === 'bind').length,
          created: fit.filter((entry) => entry.resolution === 'create').length,
          needsAdapt: fit.filter((entry) => entry.resolution === 'needs-adapt').length,
        };
      } catch {
        // Advisory only — a malformed envelope must not break the palette.
      }
    }
    return summaries;
  }, [blockLibraryExamples.examples, strategyVariables]);
  // Server-resolved grounding summary for the generation dialog — the honest
  // "what the AI will actually see" panel that replaced the dead data pickers
  // (the server ignores request-side picker selections on snapshot strategies).
  const screenGenerationGrounding = useMemo(() => {
    const block = selectedScreenGenerationBlock as unknown as Record<string, any> | null;
    if (!block || !strategyDoc) return undefined;
    const variables = (strategyDoc.variables ?? {}) as Record<string, any>;
    const routerEntry = Object.values(variables).find((variable: any) =>
      Array.isArray(variable?.tags) && variable.tags.includes('router')) as Record<string, any> | undefined;
    const routerVariableId = routerEntry?.id as string | undefined;
    const routerOptions: Array<{ id?: string; label?: string }> = Array.isArray(routerEntry?.options)
      ? routerEntry!.options : [];

    const contractOutputs: Array<Record<string, any>> = Array.isArray(block.blockContract?.outputAssignment)
      ? block.blockContract.outputAssignment : [];
    const personalization = (block.personalizationContext ?? {}) as Record<string, any>;
    // Union of both declarations: personalizationContext can lag behind the
    // contract (the router's email output was added by repair to the contract
    // only) — either source alone under-reports.
    const collectedIds: string[] = [...new Set([
      ...(Array.isArray(personalization.currentOutputVariableIds) ? personalization.currentOutputVariableIds : []),
      ...contractOutputs.map((entry) => entry?.variableId ?? entry?.id).filter(Boolean),
    ])];
    const isRouterBlock = Boolean(routerVariableId && (
      collectedIds.includes(routerVariableId)
      || (Array.isArray(block.action?.edges) && block.action.edges.some((edge: any) =>
        (edge?.conditions ?? edge?.guards ?? []).some((condition: any) => condition?.key === routerVariableId)))
    ));

    const blockTaskId = typeof block.taskId === 'string' ? block.taskId : undefined;
    const taskRouteLabels = isRouterBlock
      ? routerOptions.map((option) => option?.label || option?.id).filter(Boolean) as string[]
      : blockTaskId
        ? [routerOptions.find((option) => option?.id === blockTaskId)?.label || blockTaskId]
        : [];

    // Field names per the strategy sourceData schema: ratings carry
    // {rating, platform, description}; bigNumbers carry {amount, description}.
    const sourceData = (strategyDoc.sourceData ?? {}) as Record<string, any>;
    const socialProof: string[] = [
      ...(Array.isArray(sourceData.ratings) ? sourceData.ratings : []).map((rating: any) => {
        const score = rating?.rating ?? rating?.value;
        const label = rating?.description ?? rating?.name ?? rating?.platform ?? '';
        return score ? `★ ${score}${label ? ` · ${label}` : ''}` : null;
      }),
      ...(Array.isArray(sourceData.bigNumbers) ? sourceData.bigNumbers : []).map((bigNumber: any) => {
        const label = bigNumber?.description ?? bigNumber?.title ?? bigNumber?.name;
        if (!label) return null;
        return bigNumber?.amount ? `${bigNumber.amount} · ${label}` : label;
      }),
    ].filter((line): line is string => Boolean(line)).slice(0, 8);

    return {
      analysisId: strategyDoc.inputManifest?.insightsAnalysisId as string | undefined,
      routerVariableId,
      taskRouteLabels,
      isRouterBlock,
      socialProof,
      variablesBefore: Array.isArray(personalization.collectedBeforeVariableIds)
        ? personalization.collectedBeforeVariableIds : [],
      variablesCollected: collectedIds.map((id) => ({ id, router: id === routerVariableId })),
    };
  }, [selectedScreenGenerationBlock, strategyDoc]);
  /**
   * HB-5: what the override accordion preloads from — the head's FROZEN thesis
   * narrowing (what the resolver itself narrows the pinned packages with), the
   * head's own source data (the base an override narrows, since an override
   * REPLACES it wholesale) and the variable package the run sends read-only.
   */
  const screenGenerationOverrideContext = useMemo(() => {
    if (!strategyDoc) return {};
    /**
     * F-LIVE2-41 (fix wave 9): the fact must state what the RUN sends, not what
     * the head happens to hold. The head's whole `variables` map read «29» while
     * the run's resolved package carried 23. `resolveHeadVariablePackage`
     * mirrors the server's two rules — the resolver's `variablePackageSource`
     * and `resolveStrategyVariablePackage`'s merge-and-filter — off the frozen
     * manifest, so the line, the chips and the split all count the same list.
     */
    const variablePackage = resolveHeadVariablePackage(strategyDoc);
    return {
      frozenInsightSelection: strategyDoc.inputManifest?.insightSelection as
        { insightIds?: string[]; categories?: string[] } | undefined,
      strategySourceData: (strategyDoc.sourceData ?? undefined) as Record<string, unknown> | undefined,
      variablePackage: {
        ...(typeof variablePackage.insightVariableCount === 'number'
          ? { insightVariableCount: variablePackage.insightVariableCount } : {}),
        ...(typeof variablePackage.projectVariableCount === 'number'
          ? { projectVariableCount: variablePackage.projectVariableCount } : {}),
        ids: variablePackage.ids,
        source: variablePackage.source,
      },
    };
  }, [strategyDoc]);
  const [healthPanelOpen, setHealthPanelOpen] = useState(false);
  const [subtypeManagerOpen, setSubtypeManagerOpen] = useState(false);
  const [healthRefreshLoading, setHealthRefreshLoading] = useState(false);
  const [stalenessVerdict, setStalenessVerdict] = useState<CliStrategyStaleness | null>(null);
  const [forkDialogOpen, setForkDialogOpen] = useState(false);
  const [forkSubmitting, setForkSubmitting] = useState(false);
  /** F-LIVE2-44: the fork's refusal/failure, shown inside its own dialog. */
  const [forkError, setForkError] = useState<string | null>(null);

  // Strategy version lifecycle (Spec A / D51): Bake / Start new version / switcher.
  const versionActions = useStrategyVersionActions({ projectId, strategyId, enabled: open });
  // R7/P2 (no-dead-end): the coordinator's loading callbacks are wired once per
  // coordinator, so they read lifecycle state through a ref — adding versionActions
  // to that effect's deps would tear the canvas down on every version-state change.
  const versionActionsRef = useRef(versionActions);
  useEffect(() => { versionActionsRef.current = versionActions; });
  const autoReopenInFlightRef = useRef(false);
  // The blocks canvas has no live listener — cherry-pick/branch rewrite the head
  // in Firestore, so bump this key to remount the canvas + recreate the coordinator.
  const [canvasReloadKey, setCanvasReloadKey] = useState(0);
  const handleHeadContentChanged = useCallback(() => {
    void refreshStrategyTheme();
    setCanvasReloadKey((k) => k + 1);
  }, [refreshStrategyTheme]);

  // ── G3d (AC-GP-014): follow a generation that is running right now ──
  // The hook reads the live task store first (the tasks panel already connects it);
  // these two are its fallbacks — a generation started from this canvas names its own
  // task, and a canvas opened mid-run takes the task from the head.
  const [sessionGenerationTaskId, setSessionGenerationTaskId] = useState<string | null>(null);
  const headGenerationTaskId = strategyDoc?.generationStatus === 'running'
    && typeof strategyDoc?.lastGeneratedTaskId === 'string'
    ? (strategyDoc.lastGeneratedTaskId as string)
    : null;
  // A block whose editor the owner has open is never refreshed under them — the
  // coordinator keeps no dirty state, the open editor IS the unsaved edit.
  const isGeneratedBlockBusy = useCallback((blockId: string) => {
    if (blockFormOpen && selectedBlockForEdit
      && (selectedBlockForEdit.id === blockId || selectedBlockForEdit.entityId === blockId)) {
      return true;
    }
    if (!screensModalOpen || !selectedBlockIdForScreens) return false;
    if (selectedBlockIdForScreens === blockId) return true;
    const openBlock = blockRegistry.get(selectedBlockIdForScreens)
      ?? blockRegistry.getAll().find((candidate) => candidate.id === selectedBlockIdForScreens
        || candidate.entityId === selectedBlockIdForScreens);
    return Boolean(openBlock && (openBlock.id === blockId || openBlock.entityId === blockId));
  }, [blockFormOpen, selectedBlockForEdit, screensModalOpen, selectedBlockIdForScreens]);
  const refreshGeneratedBlock = useCallback((blockId: string) => (
    coordinatorRef.current?.refreshBlockFromFirebase(blockId, { selectNewNode: false })
  ), []);
  // A burst (the planned blocks of a run) costs ONE collection read, not N.
  const refreshGeneratedBlocks = useCallback((blockIds: string[]) => (
    coordinatorRef.current?.refreshBlocksFromFirebase(blockIds, { selectNewNode: false })
  ), []);
  // The run ended: ONE full canvas refresh — it also picks up the previous
  // generation's blocks, which a completed regeneration deletes at the very end.
  const handleGenerationRunEnd = useCallback(() => {
    setSessionGenerationTaskId(null);
    setCanvasReloadKey((key) => key + 1);
  }, []);
  useFollowStrategyGeneration({
    projectId,
    strategyId,
    enabled: open && Boolean(coordinator),
    fallbackTaskId: sessionGenerationTaskId ?? headGenerationTaskId,
    refreshBlock: refreshGeneratedBlock,
    refreshBlocks: refreshGeneratedBlocks,
    onRunEnd: handleGenerationRunEnd,
    isBlockBusy: isGeneratedBlockBusy,
  });
  const [bakeDialogOpen, setBakeDialogOpen] = useState(false);
  // Spec C (C4): insight-impact report dialog (Mode 3 dry-run).
  const [insightImpactOpen, setInsightImpactOpen] = useState(false);
  // Spec D4 (7b): ?impactFrom&impactTo deep link (experiment card [View diff])
  // opens the impact dialog on that explicit pair. Mount-only read.
  const [impactPair, setImpactPair] = useState<{ from: string; to: string } | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('impactFrom');
    const to = params.get('impactTo');
    if (from && to) {
      setImpactPair({ from, to });
      setInsightImpactOpen(true);
    }
  }, []);
  const [bakeName, setBakeName] = useState('');
  // U8 (Spec D2): operator note on what changed, stored on the version node.
  const [bakeDescription, setBakeDescription] = useState('');
  const handleOpenBakeDialog = useCallback(() => {
    setBakeName(`v${versionActions.versions.length + 1}`);
    setBakeDescription('');
    setBakeDialogOpen(true);
  }, [versionActions.versions.length]);
  const handleConfirmBake = useCallback(async () => {
    const created = await versionActions.bake(bakeName.trim() || undefined, bakeDescription.trim() || undefined);
    if (created) setBakeDialogOpen(false);
  }, [versionActions, bakeName, bakeDescription]);
  // Declared after versionActions (hook order): the success path refreshes the
  // version switcher because a compile can auto-bake a draft head (R7/P1).
  const handleCompileToOnboarding = useCallback(async (payload: StrategyCompilePayload) => {
    setCompileLoading(true);
    // G3c fix round 1 (M3): the dialog is the visible surface only while it is open.
    // Everything up to and including the compile request answers INTO it; once the
    // compile succeeded and the dialog closed, a later failure has no alert left and
    // goes back to the canvas notice.
    let dialogOpen = true;
    try {
      const api = new AgentApiClient();
      const response = await api.compileStrategyToOnboarding({
        projectId,
        strategyId,
        ...payload,
      });

      if (!response.success) {
        throw new Error(response.error || 'Compile failed');
      }

      setCompileDialogOpen(false);
      dialogOpen = false;
      if (response.editorUrl) {
        window.open(response.editorUrl, '_blank', 'noopener,noreferrer');
      }

      const autoBakeNote = response.autoBaked
        ? `Baked ${response.autoBakedVersionName || 'a version'} automatically. `
        : '';
      setSyncNotice({
        message: `${autoBakeNote}Onboarding created with ${response.screenCount ?? 0} screens.`,
        severity: 'success',
      });
      if (response.autoBaked) {
        await versionActions.refresh();
      }
      // Spec D4 (U6): the persistent "Onboarding ↗" chip keys on
      // strategyDoc.lastCompiledTo — refresh it so the chip appears after the
      // FIRST compile without a reload.
      new StrategyService(projectId).get(strategyId)
        .then((strategy) => setStrategyDoc(strategy as any))
        .catch(() => { /* chip appears on next open — not an error */ });
    } catch (err) {
      console.error('[BlockCanvasEditor] Compile to onboarding failed:', err);
      // G3c (owner rule 2026-09-11): while the dialog is open the message goes back to
      // its inline alert — a snackbar rendered behind the modal (inside the transformed
      // canvas) can be invisible. After it closed the notice is the only surface left.
      if (dialogOpen) {
        return { error: (err as Error).message || 'Compile failed' };
      }
      setSyncNotice({ message: `Compile failed: ${(err as Error).message}`, severity: 'error' });
    } finally {
      setCompileLoading(false);
    }
  }, [projectId, strategyId, versionActions]);
  useEffect(() => {
    if (!open || !projectId || !strategyId) return;
    let cancelled = false;
    const strategyService = new StrategyService(projectId);
    strategyService.get(strategyId).then(strategy => {
      if (cancelled) return;
      setStrategyVariables((strategy as any)?.variables || {});
      setStrategyDoc(strategy as any);
      const manifest = (strategy as any)?.inputManifest;
      if (manifest) {
        // Staleness is a signal, not an error — failures stay silent (D7/D27).
        new StrategyStalenessService(projectId)
          .compute(strategy as any)
          .then(verdict => { if (!cancelled) setStalenessVerdict(verdict); })
          .catch(() => { if (!cancelled) setStalenessVerdict(null); });
      } else {
        setStalenessVerdict(null);
      }
    }).catch(err => {
      console.warn('[BlockCanvasEditor] Failed to load strategy variables:', err);
    });
    return () => { cancelled = true; };
  }, [open, projectId, strategyId, canvasReloadKey]);

  const strategyHealthReport = strategyDoc?.strategyHealthReport as CliStrategyHealthReport | undefined;

  const handleRefreshStrategyHealth = useCallback(async () => {
    setHealthRefreshLoading(true);
    try {
      const response = await new AgentApiClient().compileStrategyToOnboarding({
        projectId,
        strategyId,
        dryRun: true,
      });
      if (!response.success || !response.strategyHealthReport) {
        throw new Error(response.error || 'Health verification did not return a report');
      }
      setStrategyDoc((current) => ({
        ...(current ?? {}),
        strategyHealthReport: response.strategyHealthReport,
      }));
    } catch (error) {
      setSyncNotice({
        message: `Health refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setHealthRefreshLoading(false);
    }
  }, [projectId, strategyId]);

  const handleHealthFindingGo = useCallback((finding: CliStrategyHealthFinding) => {
    const target = resolveCliStrategyHealthFindingTarget(finding);
    const targetBlock = blockRegistry.getAll().find((block) => (
      target.blockKey && (block.id === target.blockKey || block.entityId === target.blockKey)
    ));
    const targetEntityId = targetBlock?.entityId;
    if (!targetEntityId || !canvasHandle) {
      setSyncNotice({
        message: 'The finding target is not currently present on the blocks canvas.',
        severity: 'info',
      });
      return;
    }
    canvasHandle.focusOnScreen(targetEntityId, 1.2);
    canvasHandle.selectNodes([targetEntityId]);
  }, [canvasHandle]);

  const handleStrategyRepair = useCallback(async (request: {
    blockId?: string;
    screenId?: string;
    apply?: boolean;
    approvedPlanChecksum?: `sha256:${string}`;
    resetAttemptLedger?: boolean;
  }) => {
    const response = await new AgentApiClient().repairStrategy({
      projectId,
      strategyId,
      ...request,
    });
    if (!('data' in response)) {
      throw new Error(response.error || 'Strategy repair failed.');
    }
    return response.data;
  }, [projectId, strategyId]);

  const handleForkSubmit = useCallback(async (input: { name: string; forkHypothesis: string }) => {
    if (!strategyDoc) return;
    setForkSubmitting(true);
    /**
     * F-LIVE2-44: the fork stops speaking through `window.alert()` — the third
     * sighting of the channel F-LIVE2-23a / F-LIVE2-28 flagged. Completion goes
     * to the canvas' own Snackbar (the one compile / lift / push already use);
     * a refusal or a failure stays INSIDE the dialog, which stays open so the
     * fix is one step away.
     */
    setForkError(null);
    try {
      const result = await new StrategyForkService(projectId).forkOnFreshInputs(
        { ...(strategyDoc as any), id: strategyId },
        {
          name: input.name,
          forkHypothesis: input.forkHypothesis,
          insightsAnalysisId: stalenessVerdict?.insightsAnalysis.latestCompleted,
        },
      );
      if (result.success) {
        setForkDialogOpen(false);
        setSyncNotice({
          severity: 'success',
          message: `Fork "${input.name}" created — AI generation started`
            + `${result.taskId ? ` (task ${result.taskId})` : ''}. Find it in the strategies list.`,
        });
      } else if (result.strategy) {
        // W1 (F-LIVE2-40): the strategy survives a non-refusal failure, so the
        // sentence stays true — only the channel changed.
        setForkError(`The strategy was created but generation did not start: ${result.error ?? 'unknown error'}`);
      } else {
        setForkError(result.error ?? 'unknown error');
      }
    } catch (error) {
      setForkError((error as Error).message);
    } finally {
      setForkSubmitting(false);
    }
  }, [projectId, strategyId, strategyDoc, stalenessVerdict]);

  // Debug logging
  useEffect(() => {
    if (open) {
      console.log('[BlockCanvasEditor] Props received:', {
        projectId,
        strategyId,
        strategyName: strategyName || 'Unnamed Strategy',
      });
    }
  }, [open, projectId, strategyId, strategyName]);

  // Event handler context
  const eventHandlerContext = useMemo<EventHandlerContext | null>(() => {
    if (!coordinator) return null;

    return {
      get setSelectedScreenIds() { return settersRef.current.setSelectedScreenIds; },
      get setIsSyncing() { return settersRef.current.setIsSyncing; },
      get setSyncOperation() { return settersRef.current.setSyncOperation; },
      coordinator: coordinator as any,
      commandManager,
      logEvent: () => {}, // No logging in production
      showConfirmation,
    };
  }, [coordinator, commandManager, showConfirmation, settersRef]);

  // Edge dialog state for event handlers hook
  const edgeDialogState = useMemo<EdgeDialogState>(() => ({
    setEditorOpen,
    setSelectedEdge,
    setShowEdgeDeleteConfirm,
    setEdgesToDelete,
    edgesToDelete,
  }), [edgesToDelete]);

  // Event handlers via shared hook
  const {
    handleScreensSelected,
    handleScreensMoved: handleScreensMovedBase,
    handleConnectionCompleted: handleConnectionCompletedBase,
    handleScreenDropped,
    handleScreensDeleted,
    handleEdgesDeleted,
    confirmEdgeDeletion: confirmEdgeDeletionBase,
    handleEdgeClicked,
    handleEdgeDeleteFromDialog: handleEdgeDeleteFromDialogBase,
    handleScreenSaved,
  } = useCanvasEventHandlers('blocks', eventHandlerContext, blockRegistry, edgeDialogState);

  // Wrapper to convert screenId → entityId for ReactFlowCanvas compatibility
  const handleScreensMoved = useCallback(async (screens: Array<{ screenId: string; newPosition: { x: number; y: number } }>) => {
    const movedEntities = screens.map(s => ({ entityId: s.screenId, newPosition: s.newPosition }));
    await handleScreensMovedBase(movedEntities);
    setLibraryInspectorRevision((revision) => revision + 1);
  }, [handleScreensMovedBase]);

  const handleConnectionCompleted = useCallback(async (payload: {
    sourceEntityId: string;
    targetEntityId: string;
    sourceActionKey: string;
  }) => {
    await handleConnectionCompletedBase(payload);
    setLibraryInspectorRevision((revision) => revision + 1);
  }, [handleConnectionCompletedBase]);

  const confirmEdgeDeletion = useCallback(async () => {
    await confirmEdgeDeletionBase();
    setLibraryInspectorRevision((revision) => revision + 1);
  }, [confirmEdgeDeletionBase]);

  const handleEdgeDeleteFromDialog = useCallback(async (edgeId: string) => {
    await handleEdgeDeleteFromDialogBase(edgeId);
    setLibraryInspectorRevision((revision) => revision + 1);
  }, [handleEdgeDeleteFromDialogBase]);

  // UXR C1/C3: click-to-add drops at the viewport center through the SAME
  // creation path as drag-and-drop (mirrors the onboarding rail behavior).
  const handleTemplateAddAtCenter = useCallback(async (templateId: string) => {
    const position = canvasHandle?.getViewportCenterPosition?.() ?? { x: 0, y: 0 };
    await handleScreenDropped({ templateId, position, snapToGrid: true });
  }, [canvasHandle, handleScreenDropped]);

  // UXR C3: keep the coordinator's subtype snapshot live for `subtype::` drops.
  useEffect(() => {
    coordinator?.setBlockSubtypes(blockSubtypeCatalog.subtypes);
  }, [coordinator, blockSubtypeCatalog.subtypes]);

  // Block Examples Library: fit gate for `example::` inserts. EVERY insert
  // confirms through the dialog — the adaptation model is chosen there
  // (operator decision 2026-08-05, no silent default). Clean fits (and
  // not-yet-synced examples) get the one-click variant with no mapping rows.
  useEffect(() => {
    if (!coordinator) return;
    coordinator.setExampleInsertGate(async ({ exampleId, source }) => {
      const tier = source === 'system' ? systemBlockLibrary.examples : blockLibraryExamples.examples;
      const example = tier.find((item) => item.id === exampleId) ?? null;
      const fit = example
        ? computeContractFit(example, strategyVariables as Record<string, unknown>)
        : [];
      // Clean fit (all bind-or-create, zero candidates): the server
      // auto-resolution needs no operator input — the dialog renders the
      // ONE-CLICK variant (no mapping rows; omitted mapping = server
      // auto-resolution, identical outcome).
      const clean = fit.every((entry) =>
        (entry.resolution === 'bind' || entry.resolution === 'create')
        && !(entry.candidates && entry.candidates.length > 0));
      return new Promise((resolve) => {
        setInsertExampleDialog({
          example,
          exampleName: example?.name ?? exampleId,
          fit: clean ? [] : fit,
          resolve,
        });
      });
    });
    return () => coordinator.setExampleInsertGate(null);
  }, [coordinator, blockLibraryExamples.examples, systemBlockLibrary.examples, strategyVariables]);

  // Pending-roundtrip listener → snackbar state.
  useEffect(() => {
    if (!coordinator) return;
    coordinator.setExampleInsertPendingListener(setExampleInsertPending);
    return () => coordinator.setExampleInsertPendingListener(null);
  }, [coordinator]);

  // AC-P1-069: block ids with a RUNNING adaptation task — FlowBlockNode
  // renders the node-level «AI adapting…» state from this set.
  const [adaptingBlockIds, setAdaptingBlockIds] = useState<ReadonlySet<string>>(new Set());
  const trackAdaptingBlock = useCallback((blockId: string, taskId: string) => {
    setAdaptingBlockIds((previous) => new Set([...previous, blockId]));
    const unsubscribe = taskFirestoreService.subscribe(
      projectId,
      taskId,
      (task) => {
        if (!task || !TERMINAL_STATUSES.includes(task.status)) return;
        unsubscribe();
        setAdaptingBlockIds((previous) => {
          const next = new Set(previous);
          next.delete(blockId);
          return next;
        });
        void coordinatorRef.current?.refreshBlockFromFirebase(blockId)
          .then(() => setLibraryInspectorRevision((revision) => revision + 1))
          .catch(() => undefined);
      },
      () => {
        unsubscribe();
        setAdaptingBlockIds((previous) => {
          const next = new Set(previous);
          next.delete(blockId);
          return next;
        });
      },
    );
  }, [projectId]);

  const selectedLibraryDraft = useMemo(() => {
    if (selectedScreenIds.length !== 1) return null;
    const selectedId = selectedScreenIds[0];
    const block = blockRegistry.get(selectedId)
      ?? blockRegistry.getAll().find((candidate) => candidate.id === selectedId);
    if (!block) return null;
    const adaptation = readLibraryAdaptationState(block);
    if (!adaptation || adaptation.mode !== 'staged' || adaptation.status === 'adapted') return null;
    return block;
  // Registry writes are observable to nodes; terminal task refreshes bump the
  // explicit revision so the inspector switches back to the template panel.
  }, [libraryInspectorRevision, selectedScreenIds]);

  const libraryInspectorContextRevision = useMemo(() => JSON.stringify({
    canvas: libraryInspectorRevision,
    strategy: strategyDoc,
    variables: strategyVariables,
    theme: strategyThemeData,
  }), [libraryInspectorRevision, strategyDoc, strategyThemeData, strategyVariables]);

  const handleLibraryAdaptationStarted = useCallback((taskId: string, blockId: string) => {
    trackAdaptingBlock(blockId, taskId);
    setSyncNotice({
      message: 'Library adaptation started. The draft will be saved only after full validation.',
      severity: 'success',
    });
  }, [trackAdaptingBlock]);

  const handleRemoveLibraryDraft = useCallback(async () => {
    if (!selectedLibraryDraft) return;
    const confirmed = await showConfirmation(
      'Remove this library draft?',
      'The staged shell has no generated screens. Its current connections will also be removed.',
    );
    if (!confirmed) return;
    const entityId = selectedLibraryDraft.entityId || selectedLibraryDraft.id;
    if (!entityId) return;
    if (!coordinator) return;
    await coordinator.deleteLibraryDraft(entityId);
    setSelectedScreenIds([]);
  }, [coordinator, selectedLibraryDraft, setSelectedScreenIds, showConfirmation]);

  // Perceivable feedback for the dialog-less clean-fit insert (FP-1): a
  // confirmation toast naming the example (the node itself arrives selected).
  useEffect(() => {
    if (!coordinator) return;
    coordinator.setExampleInsertedListener(({ blockId, name, exampleId, source, mode, adapt, adaptTaskId }) => {
      const tier = source === 'system' ? systemBlockLibrary.examples : blockLibraryExamples.examples;
      const example = tier.find((item) => item.id === exampleId);
      const label = example?.name ?? name;
      if (adapt && adaptTaskId && blockId) {
        trackAdaptingBlock(blockId, adaptTaskId);
      }
      setSyncNotice({
        message: mode === 'staged'
          ? `Added «${label}» as a library draft. Place, connect, then adapt it.`
          : adapt && adaptTaskId
          ? `Adapting «${label}» before insertion. It will appear only after validation succeeds.`
          : `Inserted «${label}» as-is. Review its structural mappings before use.`,
        severity: 'success',
      });
    });
    return () => coordinator.setExampleInsertedListener(null);
  }, [coordinator, blockLibraryExamples.examples, systemBlockLibrary.examples, trackAdaptingBlock]);

  // ── UXR C4: Generate for the SAVED strategy (canvas Actions entry) ──
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false);
  const [generateSubmitting, setGenerateSubmitting] = useState(false);
  const [generateScreensChoice, setGenerateScreensChoice] = useState(true);
  // D51/AC-016: a baked/published head is read-only — the route would refuse the
  // content write, so the entry is disabled with the reason instead of failing late.
  const generateDisabledReason = versionActions.status !== 'draft'
    ? 'This version is baked (read-only). Start New Version first.'
    : undefined;

  const handleConfirmGenerate = useCallback(async () => {
    if (!strategyDoc) return;
    setGenerateSubmitting(true);
    try {
      const hydrator = new SourceDataHydrator(
        projectId,
        new AudienceService(projectId, audienceConverter),
        new TaskService(projectId, taskConverter),
        new StudyCaseService(projectId, studyCaseConverter),
        new ReviewService(projectId),
        new RatingService(projectId),
        new BigNumberService(projectId),
      );
      const orchestratorService = new StrategyOrchestrator(projectId, strategyService, hydrator);
      const result = await orchestratorService.generateForExistingStrategy(
        { ...(strategyDoc as Record<string, unknown>), id: strategyId } as never,
        { generateScreens: generateScreensChoice },
      );
      if (!result.success) {
        // AC-044: a failed START leaves the saved strategy untouched — surface and stop.
        window.alert(`Generation failed to start: ${result.error ?? 'Unknown error'}\n\nThe existing blocks and screens are unchanged.`);
        return;
      }
      // G3d: the canvas follows this run — its blocks appear as the server saves them.
      if (result.taskId) setSessionGenerationTaskId(result.taskId);
      setGenerateConfirmOpen(false);
    } finally {
      setGenerateSubmitting(false);
    }
  }, [strategyDoc, projectId, strategyService, strategyId, generateScreensChoice]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyboard = async (e: KeyboardEvent) => {
      // Don't intercept shortcuts when a dialog or input field is active
      if (isKeyboardCapturedByOverlay(e)) return;

      // Cmd+Z (Mac) or Ctrl+Z (Windows/Linux) = Undo
      // Cmd+Shift+Z (Mac) or Ctrl+Shift+Z (Windows/Linux) = Redo
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault(); // Prevent browser default undo

        if (e.shiftKey) {
          // Redo
          if (commandManager.canRedo()) {
            await commandManager.redo();
          }
        } else {
          // Undo
          if (commandManager.canUndo()) {
            await commandManager.undo();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [commandManager]);

  // Handle opening block editor
  const handleOpenBlockEditor = useCallback((blockId: string) => {
    const block = blockRegistry.get(blockId);
    if (!block) {
      console.warn('[BlockCanvasEditor] Block not found in registry:', blockId);
      return;
    }

    if (!block.id) {
      console.error('[BlockCanvasEditor] Block missing Firestore ID:', {
        entityId: blockId,
        blockName: (block as MaybeLegacyBlock).blockName || block.name,
      });
      return;
    }

    console.log('[BlockCanvasEditor] Opening BlockFormDialog for edit:', {
      entityId: blockId,
      firestoreId: block.id,
      name: (block as MaybeLegacyBlock).blockName || block.name,
      projectId,
      strategyId,
    });

    setSelectedBlockForEdit(block as StrategyBlock);
    setBlockFormOpen(true);
  }, [projectId, strategyId]);

  // Handle block form submit (uses EditEntityCommand for undo/redo)
  const handleBlockFormSubmit = useCallback(async (updatedBlock: StrategyBlock): Promise<{ success: boolean; error?: Error }> => {
    if (!eventHandlerContext) {
      return { success: false, error: new Error('Event handler context not available') };
    }

    try {
      console.log('[BlockCanvasEditor] Block form submit - creating EditEntityCommand:', {
        entityId: updatedBlock.entityId,
        firestoreId: updatedBlock.id,
        name: updatedBlock.name,
      });

      const command = new EditEntityCommand<StrategyBlock, StrategyBlockService>(
        updatedBlock.entityId!,
        updatedBlock,
        eventHandlerContext as any,
      );

      await commandManager.execute(command);

      setBlockFormOpen(false);
      setSelectedBlockForEdit(null);

      return { success: true };
    } catch (error) {
      console.error('[BlockCanvasEditor] Block form submit error:', error);
      return { success: false, error: error as Error };
    }
  }, [eventHandlerContext, commandManager]);

  const resolveBlockForScreens = useCallback((blockRef: string): StrategyBlock | undefined => (
    blockRegistry.get(blockRef)
    || blockRegistry.getAll().find(block => block.id === blockRef || block.entityId === blockRef)
  ), []);

  const openScreensModalForBlockRef = useCallback((blockRef: string, source: 'local' | 'url') => {
    const block = resolveBlockForScreens(blockRef);
    if (!block) {
      console.warn('[BlockCanvasEditor] Block not found for screens modal:', blockRef);
      return false;
    }

    if (!block.id) {
      console.error('[BlockCanvasEditor] Block missing Firestore ID for screens modal:', {
        blockRef,
        entityId: block.entityId,
        blockName: (block as MaybeLegacyBlock).blockName || block.name,
      });
      return false;
    }

    const blockName = (block as MaybeLegacyBlock).blockName || block.name || 'Unnamed Block';
    setSelectedBlockIdForScreens(block.id);
    setSelectedBlockNameForScreens(blockName);
    setScreensModalSource(source);
    setScreensModalOpen(true);
    return true;
  }, [resolveBlockForScreens]);

  const handleCloseScreensModal = useCallback(() => {
    const shouldNotifyRoute = screensModalSource === 'url';
    setScreensModalOpen(false);
    setSelectedBlockIdForScreens('');
    setSelectedBlockNameForScreens('');
    setScreensModalSource(null);
    if (shouldNotifyRoute) {
      onScreensModalClose?.();
    }
  }, [onScreensModalClose, screensModalSource]);

  const selectedBlockForScreens = useMemo(() => (
    selectedBlockIdForScreens ? resolveBlockForScreens(selectedBlockIdForScreens) : undefined
  ), [resolveBlockForScreens, selectedBlockIdForScreens]);

  useEffect(() => {
    if (!open) return;

    if (!initialScreensBlockId) {
      if (screensModalSource === 'url') {
        setScreensModalOpen(false);
        setSelectedBlockIdForScreens('');
        setSelectedBlockNameForScreens('');
        setScreensModalSource(null);
      }
      return;
    }

    const selectedBlock = selectedBlockIdForScreens
      ? resolveBlockForScreens(selectedBlockIdForScreens)
      : undefined;
    const selectedMatchesRequested = Boolean(
      selectedBlock
      && (selectedBlock.id === initialScreensBlockId || selectedBlock.entityId === initialScreensBlockId)
    );

    if (screensModalOpen && screensModalSource === 'url' && selectedMatchesRequested) {
      return;
    }

    openScreensModalForBlockRef(initialScreensBlockId, 'url');
  }, [
    initialScreensBlockId,
    isLoading,
    open,
    openScreensModalForBlockRef,
    resolveBlockForScreens,
    screensModalOpen,
    screensModalSource,
    selectedBlockIdForScreens,
  ]);

  // Handle node double-click
  const handleNodeDoubleClick = useCallback((entityId: string) => {
    // Blocks mode: double-click block → open screens modal
    const block = blockRegistry.get(entityId);
    if (!block) {
      console.warn('[BlockCanvasEditor] Block not found for double-click:', entityId);
      return;
    }

    const blockName = (block as MaybeLegacyBlock).blockName || block.name || 'Unnamed Block';
    console.log('[BlockCanvasEditor] Block double-clicked - opening screens modal:', {
      entityId,
      firestoreId: block.id,
      blockName,
    });

    if (onOpenBlockScreens && block.id) {
      onOpenBlockScreens(block.id);
      return;
    }

    openScreensModalForBlockRef(entityId, 'local');
  }, [onOpenBlockScreens, openScreensModalForBlockRef]);

  // Handle node action
  const handleNodeAction = useCallback(async (entityId: string, actionKey: string, entityType?: 'block' | 'screen') => {
    // Intercept edit action for blocks
    if (actionKey === 'edit' && entityType === 'block') {
      handleOpenBlockEditor(entityId);
      return;
    }

    // Open block screens in a new tab
    if (actionKey === 'open-in-tab' && entityType === 'block') {
      const block = blockRegistry.get(entityId);
      if (block?.id) {
        window.open(`/project/${projectId}/strategy/${strategyId}/block/${block.id}`, '_blank');
      }
      return;
    }

    // Generate screens with AI for this block
    if (actionKey === 'generate-screens' && entityType === 'block') {
      const block = blockRegistry.get(entityId);
      if (!block?.id) return;
      setScreenGenerationBlockId(entityId);
      return;
    }

    // C7-2: rich clone with edge policy — opens the options popover anchored
    // at the node; the actual work runs through CloneBlockAdvancedCommand.
    if (actionKey === 'clone-block' && entityType === 'block') {
      const block = blockRegistry.get(entityId);
      if (!block?.id) return;
      // Live nodes carry `node-<blockDocId>` (recon-verified); older paths used
      // the entityId — try both before falling back to a centered position.
      const nodeRect = (
        document.querySelector(`[data-testid="node-${block.id}"]`)
        ?? document.querySelector(`[data-testid="node-${entityId}"]`)
      )?.getBoundingClientRect();
      setCloneBlockTarget({
        entityId,
        blockId: block.id,
        name: block.name || 'block',
        anchorPosition: nodeRect
          ? { top: Math.max(nodeRect.top, 16), left: nodeRect.right + 12 }
          : { top: window.innerHeight / 3, left: window.innerWidth / 2 },
      });
      return;
    }

    // Block Examples Library: capture the block into the project library.
    if (actionKey === 'save-as-example' && entityType === 'block') {
      const block = blockRegistry.get(entityId);
      if (!block?.id) return;
      setSaveAsExampleTarget({ blockId: block.id, name: block.name || 'Block' });
      return;
    }

    // Spec D4 (U11): rebase a cherry-picked block onto the current insights.
    // The server composes the plan (range diff, vanished citations); the UI
    // forwards prompt + changeDiff + promptMode verbatim. The regen is a
    // normal block task (tray-visible); the chip clears on the block refresh.
    if (actionKey === 'rebase-block' && entityType === 'block') {
      const block = blockRegistry.get(entityId);
      if (!block?.id) return;
      try {
        const { StrategyBlockRebaseService } = await import('@modules/strategy-blocks/services/StrategyBlockRebaseService');
        const result = await new StrategyBlockRebaseService(projectId, strategyId).dispatchRebase(block.id);
        setSyncNotice({ message: result.note, severity: result.dispatched ? 'success' : 'info' });
      } catch (err) {
        console.error('[BlockCanvasEditor] Block rebase dispatch failed:', err);
        setSyncNotice({ message: `Rebase failed: ${(err as Error).message}`, severity: 'error' });
      }
      return;
    }

    // Forward all other actions to event handler
    if (!eventHandlerContext) return;

    const flowCanvasEventHandler = await import('@modules/reactFlow/services/shared/FlowCanvasEventHandler').then(m => m.flowCanvasEventHandler);
    await flowCanvasEventHandler.handleEvent({
      type: 'node.action',
      timestamp: Date.now(),
      entityId,
      actionKey,
      entityType: 'block',
    } as any, eventHandlerContext);
  }, [eventHandlerContext, handleOpenBlockEditor, projectId, strategyId]);

  // C7-2: execute the rich clone through the undoable command. The command
  // talks to the server clone API and, for fork-incoming, patches predecessor
  // edges through the coordinator so undo can revert both sides.
  const handleCloneBlockConfirm = useCallback(async (options: CloneBlockPopoverSubmit) => {
    if (!cloneBlockTarget) return;
    setCloneBusy(true);
    try {
      const [{ StrategyBlockCloneService }, { CloneBlockAdvancedCommand }] = await Promise.all([
        import('@modules/strategy-blocks/services/StrategyBlockCloneService'),
        import('@modules/reactFlow/commands/CloneBlockAdvancedCommand'),
      ]);
      const service = new StrategyBlockCloneService(projectId, strategyId);
      const command = new CloneBlockAdvancedCommand(
        cloneBlockTarget.blockId,
        cloneBlockTarget.entityId,
        options,
        service,
        {
          listBlocks: () => blockRegistry.getAll(),
          updateBlockEdges: async (block, edges) => {
            const coord = coordinatorRef.current;
            if (!coord || !block.id) throw new Error('Canvas is not ready');
            const updated = {
              ...block,
              action: { ...((block as { action?: Record<string, unknown> }).action ?? {}), edges },
            } as StrategyBlock;
            // D-LIVE2-3: the registry, the canvas nodes and `updateNodeData` are
            // all keyed by entityId — passing the Firestore doc id made EVERY
            // fork-incoming clone throw «Block not found» AFTER the clone was
            // already written on the server.
            await coord.updateEntityAndRefreshConnections(block.entityId ?? block.id, updated);
          },
          deleteBlocks: async (entityIds) => {
            const coord = coordinatorRef.current;
            if (!coord) throw new Error('Canvas is not ready');
            await coord.deleteEntities(entityIds);
          },
          reloadCanvas: () => setCanvasReloadKey((key) => key + 1),
        },
      );
      const result = await commandManager.execute(command);
      if (!result.success) throw result.error ?? new Error('Clone failed');
      setSyncNotice({
        message: options.incomingMode === 'fork'
          ? 'Block cloned — incoming edges forked to the clone. Undo available.'
          : 'Block cloned (disconnected-in) — wire an incoming edge or use it for variant comparison. Undo available.',
        severity: 'success',
      });
      setCloneBlockTarget(null);
    } catch (err) {
      console.error('[BlockCanvasEditor] Clone block failed:', err);
      setSyncNotice({ message: `Clone failed: ${(err as Error).message}`, severity: 'error' });
    } finally {
      setCloneBusy(false);
    }
  }, [cloneBlockTarget, projectId, strategyId, commandManager]);

  // C7-4: sequential variant fan-out — ONE parent task; the canvas reloads on
  // its terminal status so all variant nodes appear together.
  const handleCloneVariantsConfirm = useCallback(async (options: CloneBlockVariantsSubmit) => {
    if (!cloneBlockTarget) return;
    setCloneBusy(true);
    try {
      const { StrategyBlockCloneService } = await import('@modules/strategy-blocks/services/StrategyBlockCloneService');
      const service = new StrategyBlockCloneService(projectId, strategyId);
      const { taskId } = await service.cloneVariants(cloneBlockTarget.blockId, {
        variantCount: options.variantCount,
        angleHints: options.angleHints,
        copyScreens: options.copyScreens,
        preserveEdges: options.keepOutgoing,
        // User-chosen model from the popover selector (connected models only)
        // — never fall back to the server default silently.
        ...(options.model ? { model: options.model } : {}),
      });
      setSyncNotice({
        message: `Variant generation started (task ${taskId}) — ${options.variantCount} variant${options.variantCount > 1 ? 's' : ''} will appear when the fan-out completes.`,
        severity: 'success',
      });
      setCloneBlockTarget(null);
      const unsubscribe = taskFirestoreService.subscribe(
        projectId,
        taskId,
        (task) => {
          if (!task || !TERMINAL_STATUSES.includes(task.status)) return;
          unsubscribe();
          setCanvasReloadKey((key) => key + 1);
          setSyncNotice({
            message: task.status === 'completed'
              ? 'Variant generation finished — canvas refreshed.'
              : 'Variant generation ended with failures — check the task tray.',
            severity: task.status === 'completed' ? 'success' : 'error',
          });
        },
        (error) => console.error('[BlockCanvasEditor] clone-variants task listener error:', error),
      );
    } catch (err) {
      console.error('[BlockCanvasEditor] Clone variants failed:', err);
      setSyncNotice({ message: `Variant generation failed: ${(err as Error).message}`, severity: 'error' });
    } finally {
      setCloneBusy(false);
    }
  }, [cloneBlockTarget, projectId, strategyId]);

  // Create coordinator
  useEffect(() => {
    if (!open) return;

    const newCoordinator = new BlockFlowCoordinator(projectId, strategyId);

    // Set loading callbacks for Firebase sync indicator
    newCoordinator.setLoadingCallbacks({
      onSyncStart: (operation) => {
        console.log(`🔄 [BlockCanvasEditor] Firebase sync START: ${operation}`);
        setIsSyncing(true);
        setSyncOperation(operation);
        // R7/P2 (no-dead-end): the first canvas mutation on a baked/published head
        // auto-reopens it as draft instead of drifting silently — the baked snapshot
        // stays frozen in versions/*, only the lifecycle pointer flips. The hook's
        // own snackbar announces "Strategy reopened for editing".
        const va = versionActionsRef.current;
        if ((va.status === 'baked' || va.status === 'published') && !autoReopenInFlightRef.current) {
          autoReopenInFlightRef.current = true;
          va.startNewVersion()
            .catch((err) => console.error('[BlockCanvasEditor] Auto-reopen after baked-head edit failed:', err))
            .finally(() => { autoReopenInFlightRef.current = false; });
        }
      },
      onSyncEnd: () => {
        console.log('✅ [BlockCanvasEditor] Firebase sync END');
        setIsSyncing(false);
        setSyncOperation('');
      },
      onSyncError: (error, operation) => {
        console.error(`❌ [BlockCanvasEditor] Firebase sync ERROR during ${operation}:`, error);
        setIsSyncing(false);
        setSyncOperation('');
      }
    });

    setCoordinator(newCoordinator);

    return () => {
      newCoordinator.destroy();
    };
    // canvasReloadKey: recreate the coordinator after cherry-pick/branch restore.
  }, [open, projectId, strategyId, setIsSyncing, setSyncOperation, canvasReloadKey]);

  // Load data and register canvas handle
  useEffect(() => {
    if (!coordinator || !canvasHandle || !open) return;

    let isActive = true;

    const initCoordinator = async () => {
      try {
        setIsLoading(true);
        await coordinator.loadBlocksFromFirebase();

        if (!isActive) return;

        coordinator.setCanvasHandle(canvasHandle);
      } catch (error) {
        console.error('[BlockCanvasEditor] Initialization error:', error);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    initCoordinator();

    return () => {
      isActive = false;
    };
  }, [coordinator, canvasHandle, open, setIsLoading]);

  // Node view factory (for rendering block previews)
  const nodeViewFactory = useCallback((entity: any) => {
    const type = entity.type?.toUpperCase() || 'BLOCK';
    const name = entity.blockName || entity.name || 'Unnamed';
    return `${type}\n${name}`;
  }, []);

  return (
    <AdaptingBlocksProvider value={adaptingBlockIds}>
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Left: Top Panel + Canvas */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top Control Panel */}
        <TopControlPanel
          strategyName={strategyName || 'Strategy'}
          currentMode="blocks"
          commandManager={commandManager}
          undoCount={commandManager.getUndoHistory().length}
          redoCount={commandManager.getRedoHistory().length}
          onClose={onClose}
          isSyncing={isSyncing}
          customActions={
            <>
            <StrategyHealthBadge
              report={strategyHealthReport}
              onClick={() => setHealthPanelOpen(true)}
            />
            {/* D3 (AC-D3-6): [What changed] opens the insight impact tree. */}
            <StrategyStalenessBadge
              verdict={stalenessVerdict}
              onFork={() => setForkDialogOpen(true)}
              onWhatChanged={() => setInsightImpactOpen(true)}
            />
            {/* Live-2 fix wave 7 (W2, F-LIVE2-25): the durable `forkLineage` keeps a
                fork readable even when its first generation saved nothing and the
                failed-run rollback removed the manifest. The manifest wins when it is
                there — it is the record of the run that produced these blocks. */}
            {(strategyDoc?.inputManifest?.predecessorStrategyId
              || strategyDoc?.forkLineage?.predecessorStrategyId) && (
              <StrategyLineageChip
                manifest={strategyDoc.inputManifest?.predecessorStrategyId
                  ? strategyDoc.inputManifest
                  : strategyDoc.forkLineage}
                generationFailed={strategyDoc.generationStatus === 'failed'
                  // Fix round 1 (M2): an empty registry while the canvas is still
                  // loading proves nothing — a failed run that DID save blocks would
                  // otherwise flash «generation failed» on the first paint.
                  && !isLoading
                  && blockRegistry.getAll().length === 0}
              />
            )}
            <StrategyDivergenceChip
              side="strategy"
              linked={Boolean(strategyDoc?.lastCompiledTo?.funnelId)}
              strategyStatus={versionActions.status}
              strategyCurrentVersion={versionActions.currentVersion}
              compiledFromVersion={strategyDoc?.lastCompiledTo?.sourceStrategyVersion}
            />
            {/* Spec D4 (U6): persistent path back to the compiled onboarding —
                replaces the one-shot post-compile snackbar as the only route. */}
            {strategyDoc?.lastCompiledTo?.funnelId && (
              <Tooltip title="Open the compiled onboarding in a new tab" arrow>
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  clickable
                  label="Onboarding ↗"
                  data-testid="strategy-open-onboarding-chip"
                  onClick={() => window.open(
                    `/project/${encodeURIComponent(projectId)}/onboarding-v2/${encodeURIComponent(String(strategyDoc.lastCompiledTo.funnelId))}`,
                    '_blank',
                    'noopener,noreferrer',
                  )}
                />
              </Tooltip>
            )}
            <StrategyVersionSwitcher actions={versionActions} onHeadContentChanged={handleHeadContentChanged} />
            <StrategyCanvasActions
              mode="blocks"
              onGenerate={() => setGenerateConfirmOpen(true)}
              generateDisabledReason={generateDisabledReason}
              onExport={handleExportStrategy}
              onImport={() => setImportDialogOpen(true)}
              onCompileToOnboarding={handleOpenCompileDialog}
              onLiftFromOnboarding={() => setLiftDialogOpen(true)}
              onPushToOnboarding={handleOpenPushDialog}
              onEditTheme={handleOpenReplaceTheme}
              onReplaceTheme={handleOpenReplaceTheme}
              onUpdateUiFromTheme={handleApplyCurrentStrategyThemeToAllScreens}
              onScoreStrategy={() => setScorecardOpen(true)}
              onBake={handleOpenBakeDialog}
              onStartNewVersion={() => versionActions.startNewVersion()}
              onInsightImpact={() => setInsightImpactOpen(true)}
              associatedFunnelId={strategyDoc?.lastCompiledTo?.funnelId
                ?? strategyDoc?.lastLiftedFrom?.funnelId
                ?? strategyDoc?.transformedFrom?.funnelId
                ?? null}
              strategyStatus={versionActions.status}
              lifecycleDisabled={versionActions.loading || versionActions.loadingVersions}
              exportDisabled={importExporting}
              compileDisabled={compileLoading}
              liftDisabled={liftLoading}
              pushDisabled={pushLoading}
              themeDisabled={themeReplaceLoading}
              replaceThemeDisabled={themeReplaceLoading}
              updateUiFromThemeDisabled={!strategyThemeData || themeReplaceLoading}
            />
            </>
          }
        />

        {/* Canvas area */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {/* Loading overlay */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  border: '4px solid rgba(255, 255, 255, 0.2)',
                  borderTop: '4px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
                Loading blocks...
              </Typography>
            </Box>
          )}

          {/* Canvas (key: full remount after cherry-pick/branch restore) */}
          <ReactFlowCanvasProvider key={canvasReloadKey}>
            <ReactFlowCanvas
              coordinator={coordinator!}
              selectedScreenIds={selectedScreenIds}
              launchScreenId={launchScreenId}
              onScreensSelected={handleScreensSelected}
              onScreensMoved={handleScreensMoved}
              onConnectionCompleted={handleConnectionCompleted}
              onScreenDropped={handleScreenDropped}
              onScreensDeleted={handleScreensDeleted}
              onEdgesDeleted={handleEdgesDeleted}
              onEdgeClicked={handleEdgeClicked}
              onNodeDoubleClick={handleNodeDoubleClick}
              onNodeAction={handleNodeAction}
              variablesById={strategyVariables}
              currentAnalysisId={strategyDoc?.inputManifest?.insightsAnalysisId}
              blockSubtypesBySlug={blockSubtypesBySlug}
              onReady={handleCanvasReady}
              nodeViewFactory={nodeViewFactory}
              gridStepSize={16}
              className="block-canvas"
            />
          </ReactFlowCanvasProvider>

          {/* Tasks Panel — bottom-left, scoped to strategy generation tasks */}
          <CanvasTasksPanel
            projectId={projectId}
            strategyId={strategyId}
            taskType={['block-update', 'generate-strategy', 'create-strategy', 'generate-image', 'strategy-asset-prompt-regeneration', 'clone-variants']}
          />

          {/* Motivation Scorecard — bottom-right, opened from the Actions menu */}
          <MotivationScorecardPanel
            open={scorecardOpen}
            onClose={() => setScorecardOpen(false)}
            projectId={projectId}
            strategyId={strategyId}
            strategyName={strategyName}
            onJumpToScreen={(ref) => {
              // Blocks canvas: focus + select the block that contains the screen.
              const target = ref.blockEntityId || ref.blockId;
              if (target && canvasHandle) {
                canvasHandle.focusOnScreen(target, 1.2);
                canvasHandle.selectNodes([target]);
              }
            }}
          />

          <StrategyHealthPanel
            open={healthPanelOpen}
            report={strategyHealthReport}
            refreshLoading={healthRefreshLoading}
            onClose={() => setHealthPanelOpen(false)}
            onRefresh={handleRefreshStrategyHealth}
            onFindingGo={handleHealthFindingGo}
            onRepair={handleStrategyRepair}
          />

          <BlockSubtypeManagerDialog
            open={subtypeManagerOpen}
            onClose={() => setSubtypeManagerOpen(false)}
            catalog={blockSubtypeCatalog}
          />

          {/* UXR C4: destructive confirm — regeneration replaces blocks/screens. */}
          {generateConfirmOpen && (
            <BaseDialog
              open={true}
              onClose={() => setGenerateConfirmOpen(false)}
              title="Generate blocks with AI"
              maxWidth="sm"
              data-testid="strategy-generate-confirm-dialog"
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Regenerate «{strategyName || 'this strategy'}» from its saved inputs?
                </Typography>
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  Existing blocks and their screens will be replaced by the new generation.
                  Variables, theme, and version history are kept. The task appears in the
                  tasks panel and the canvas updates live.
                </Typography>
                <FormControlLabel
                  sx={{ mt: 1.5 }}
                  control={(
                    <Switch
                      size="small"
                      checked={generateScreensChoice}
                      onChange={(_, checked) => setGenerateScreensChoice(checked)}
                      data-testid="strategy-generate-confirm-screens"
                    />
                  )}
                  label="Also generate screens for each block"
                  slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setGenerateConfirmOpen(false)}
                    disabled={generateSubmitting}
                    data-testid="strategy-generate-confirm-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => void handleConfirmGenerate()}
                    disabled={generateSubmitting}
                    data-testid="strategy-generate-confirm-submit"
                  >
                    {generateSubmitting ? 'Starting…' : 'Generate'}
                  </Button>
                </Box>
              </Box>
            </BaseDialog>
          )}

          {/* TransitionEditor Dialog */}
          <TransitionEditor
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            edgeData={selectedEdge}
            onEdgeDelete={handleEdgeDeleteFromDialog}
          />

          {/* Edge Delete Confirmation */}
          <EdgeDeleteConfirmation
            open={showEdgeDeleteConfirm}
            onClose={() => setShowEdgeDeleteConfirm(false)}
            onConfirm={confirmEdgeDeletion}
            message={
              edgesToDelete.length === 1
                ? 'Are you sure you want to delete this connection?'
                : `Are you sure you want to delete ${edgesToDelete.length} connections?`
            }
          />

          {/* BlockFormDialog for editing blocks */}
          <BlockFormDialog
            open={blockFormOpen}
            mode="edit"
            projectId={projectId}
            strategyId={strategyId}
            blockId={selectedBlockForEdit?.id}
            initialData={selectedBlockForEdit || undefined}
            onClose={() => {
              setBlockFormOpen(false);
              setSelectedBlockForEdit(null);
            }}
            onSave={(block: StrategyBlock) => {
              // Fallback callback (not called when onSubmit is provided)
              console.log('[BlockCanvasEditor] Block saved (fallback):', block.id);
              blockRegistry.register(block);
            }}
            onSubmit={handleBlockFormSubmit}
            onGenerationTaskStarted={subscribeToBlockTaskCompletion}
            /* HB-6: the form's «Update Block with AI» closes itself and opens
               the ONE block-update dialog (FP-4). Fix round 1 (M7): the block
               comes from the ARGUMENT, never from `selectedBlockForEdit` — the
               form clears that selection as it closes, one line earlier. The
               registry is keyed by entityId, so the form's document id is
               mapped explicitly. */
            onOpenScreenGeneration={(formBlockId: string) => {
              const registered = blockRegistry.get(formBlockId)
                ?? blockRegistry.getAll().find((candidate) => candidate.id === formBlockId);
              const entityId = (registered as unknown as Record<string, any> | undefined)?.entityId
                ?? registered?.id
                ?? formBlockId;
              setScreenGenerationBlockId(String(entityId));
            }}
          />

          {/* Set Launch Entity Confirmation Dialog */}
          {confirmDialog && (
            <BaseDialog
              open={true}
              onClose={() => {
                confirmDialog.resolve(false);
                setConfirmDialog(null);
              }}
              title="Confirmation"
              maxWidth="sm"
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {confirmDialog.message}
                </Typography>
                {confirmDialog.details && (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                    {confirmDialog.details}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      confirmDialog.resolve(true);
                      setConfirmDialog(null);
                    }}
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            </BaseDialog>
          )}
        </Box>
      </Box>

      {/* Right: a persisted staged draft owns the inspector until it adapts or
          is removed; every other selection keeps the standard template rail. */}
      {selectedLibraryDraft ? (
        <LibraryAdaptationInspector
          projectId={projectId}
          strategyId={strategyId}
          block={selectedLibraryDraft}
          contextRevision={libraryInspectorContextRevision}
          onClose={() => setSelectedScreenIds([])}
          onRemove={handleRemoveLibraryDraft}
          onTaskStarted={handleLibraryAdaptationStarted}
        />
      ) : (
        <BlockTemplatePanel
          subtypes={blockSubtypeCatalog.subtypes}
          examples={blockLibraryExamples.examples}
          fitSummaryByExample={exampleFitSummaries}
          onBrowseLibrary={() => setLibraryBrowserOpen(true)}
          onTemplateDragStart={() => {}}
          onTemplateAdd={handleTemplateAddAtCenter}
          onManageSubtypes={() => setSubtypeManagerOpen(true)}
          onExampleDetails={(exampleId) => {
            const example = blockLibraryExamples.examples.find((doc) => doc.id === exampleId);
            if (example) setExampleDetails({ example, source: 'project' });
          }}
        />
      )}

      {/* V2 Screen Canvas — shown when double-clicking a block */}
      {screensModalOpen && selectedBlockIdForScreens && (
        <OnboardingCanvasEditorV2
          open={screensModalOpen}
          onClose={handleCloseScreensModal}
          projectId={projectId}
          onboardingId=""
          versionId=""
          onboardingName={`${strategyName || 'Strategy'} > ${selectedBlockNameForScreens}`}
          coordinatorFactory={() => new StrategyV2ScreenFlowCoordinator(projectId, strategyId, selectedBlockIdForScreens)}
          editorMode="ai"
          autoOpenScreenId={initialScreensScreenId || undefined}
          hideLocaleTools
          strategyId={strategyId}
          blockId={selectedBlockIdForScreens}
          strategyBlockEntityId={selectedBlockForScreens?.entityId || selectedBlockForScreens?.id}
          externalVariables={strategyVariables}
          onScreenEditorOpen={(screenId) => {
            if (screensModalSource === 'url' && selectedBlockIdForScreens) {
              onOpenBlockScreen?.(selectedBlockIdForScreens, screenId);
            }
          }}
          onScreenEditorClose={() => {
            if (screensModalSource === 'url') {
              onScreenEditorClose?.();
            }
          }}
        />
      )}

      {/* Bake the working draft into an immutable version (Spec A / D51) */}
      <Dialog open={bakeDialogOpen} onClose={() => setBakeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Bake Version</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Version Name"
            fullWidth
            value={bakeName}
            onChange={(event) => setBakeName(event.target.value)}
            disabled={versionActions.loading}
            placeholder="e.g., v2, Baseline 2026-07"
            inputProps={{ 'data-testid': 'strategy-bake-name' }}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            value={bakeDescription}
            onChange={(event) => setBakeDescription(event.target.value)}
            disabled={versionActions.loading}
            placeholder="What changed in this version?"
            inputProps={{ 'data-testid': 'strategy-bake-description' }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Baking snapshots the current blocks, screens and theme into a read-only
            version and locks the strategy. Start a new version to edit again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBakeDialogOpen(false)} disabled={versionActions.loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBake}
            variant="contained"
            disabled={versionActions.loading}
            data-testid="strategy-bake-confirm"
          >
            Bake
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spec C (C4): insight-impact report (Mode 3 dry-run) */}
      <InsightImpactDialog
        open={insightImpactOpen}
        onClose={() => { setInsightImpactOpen(false); setImpactPair(null); }}
        projectId={projectId}
        strategyId={strategyId}
        defaultFromAnalysisId={strategyDoc?.inputManifest?.insightsAnalysisId}
        explicitPair={impactPair ?? undefined}
        strategySnapshot={strategyDoc ? {
          name: strategyDoc.name,
          description: strategyDoc.description,
          strategyType: strategyDoc.strategyType,
          toneOfVoice: strategyDoc.toneOfVoice,
          designDescription: strategyDoc.designDescription,
          sourceData: strategyDoc.sourceData,
        } : undefined}
      />

      {/* Strategy import dialog */}
        <StrategyForkDialog
          open={forkDialogOpen}
          predecessorName={strategyName || (strategyDoc?.name ?? 'Strategy')}
          verdict={stalenessVerdict}
          submitting={forkSubmitting}
          error={forkError}
          onClose={() => { setForkError(null); setForkDialogOpen(false); }}
          onSubmit={handleForkSubmit}
        />

      <StrategyImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        mode="strategy"
        projectId={projectId}
        strategyId={strategyId}
        themeFlowDocument={strategyThemeData?.flowDocument}
        onImported={handleImported}
      />

      {/* Compile strategy blocks into a full onboarding */}
      {compileDialogOpen && (
        <StrategyCompileDialog
          open={compileDialogOpen}
          projectId={projectId}
          defaultName={`${strategyName || 'Strategy'} Onboarding`}
          currentThemeAvailable={hasCurrentStrategyTheme}
          currentThemeSourceId={strategyThemeData?.sourceThemeId}
          loading={compileLoading}
          onClose={() => setCompileDialogOpen(false)}
          onPreview={handleCompilePreview}
          onCompile={handleCompileToOnboarding}
        />
      )}

      {/* Push the whole strategy into the linked onboarding in place */}
      <StrategySyncConfirmDialog
        open={pushDialogOpen}
        mode="push-strategy"
        dryRunResult={pushDryRun}
        loading={pushLoading}
        error={pushError}
        onConfirm={handleConfirmPush}
        onClose={() => setPushDialogOpen(false)}
      />

      {/* Lift onboarding version back into strategy blocks */}
      <StrategyLiftDialog
        open={liftDialogOpen}
        projectId={projectId}
        defaultStrategyId={strategyId}
        defaultName={strategyName || 'Strategy'}
        defaultFunnelId={strategyDoc?.lastCompiledTo?.funnelId
          ?? strategyDoc?.lastLiftedFrom?.funnelId
          ?? strategyDoc?.transformedFrom?.funnelId}
        loading={liftLoading}
        onClose={() => setLiftDialogOpen(false)}
        onPreview={handleLiftPreview}
        onLift={handleLiftFromOnboarding}
      />

      {/* Sync completion / failure notices (compile, lift, push) */}
      <Snackbar
        open={Boolean(syncNotice)}
        autoHideDuration={6000}
        onClose={() => setSyncNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={syncNotice?.severity ?? 'success'}
          onClose={() => setSyncNotice(null)}
          data-testid="strategy-sync-snackbar"
        >
          {syncNotice?.message}
        </Alert>
      </Snackbar>

      {/* Generate screens for the selected block */}
      {/* HB-1 client (F-LIVE-23): `strategyModel` seeds the picker from the
          strategy's own last completed generation, not the project default. */}
      <BlockScreenGenerationDialog
        open={Boolean(screenGenerationBlockId)}
        block={selectedScreenGenerationBlock}
        projectId={projectId}
        strategyId={strategyId}
        grounding={screenGenerationGrounding}
        strategyModel={typeof strategyDoc?.lastGenerationModel === 'string' ? strategyDoc.lastGenerationModel : undefined}
        {...screenGenerationOverrideContext}
        loading={screenGenerationLoading}
        onClose={handleCloseScreenGenerationDialog}
        onGenerate={handleGenerateBlockScreens}
      />

      {/* C7-2: Clone Block options popover */}
      <CloneBlockPopover
        open={Boolean(cloneBlockTarget)}
        anchorPosition={cloneBlockTarget?.anchorPosition ?? null}
        blockName={cloneBlockTarget?.name ?? ''}
        projectId={projectId}
        busy={cloneBusy}
        onClose={() => setCloneBlockTarget(null)}
        onClone={handleCloneBlockConfirm}
        onCloneVariants={handleCloneVariantsConfirm}
      />

      {/* Block Examples Library: Insert building block (EVERY insert — model
          is chosen here; clean fits show the one-click variant) */}
      {insertExampleDialog && (
        <InsertBuildingBlockDialog
          open
          projectId={projectId}
          exampleName={insertExampleDialog.exampleName}
          strategyName={strategyName}
          fit={insertExampleDialog.fit}
          variableMeta={insertExampleDialog.example ? exampleVariableMeta(insertExampleDialog.example) : {}}
          onSubmit={(decision) => {
            insertExampleDialog.resolve(decision);
            setInsertExampleDialog(null);
          }}
          onCancel={() => {
            insertExampleDialog.resolve(null);
            setInsertExampleDialog(null);
          }}
        />
      )}

      {/* Block Examples Library: example details (palette ⓘ) — preview before insert */}
      {exampleDetails && (
        <ExampleDetailsDialog
          open
          example={exampleDetails.example}
          source={exampleDetails.source}
          strategyName={strategyName}
          strategyVariables={strategyVariables as Record<string, unknown>}
          onClose={() => setExampleDetails(null)}
          onAddToCanvas={() => {
            const { example, source } = exampleDetails;
            setExampleDetails(null);
            void handleTemplateAddAtCenter(`example::${source}::${example.id}`);
          }}
          onAdaptImmediately={() => {
            const { example, source } = exampleDetails;
            setExampleDetails(null);
            void handleTemplateAddAtCenter(`${EXAMPLE_SECONDARY_TEMPLATE_ID_PREFIX}${source}::${example.id}`);
          }}
        />
      )}

      {/* Block Examples Library: from-library roundtrip pending indicator */}
      <Snackbar
        open={Boolean(exampleInsertPending)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={18} color="inherit" />}
          data-testid="example-insert-pending"
        >
          Inserting example…
        </Alert>
      </Snackbar>

      {/* Block Examples Library: browser (Import from system / Pick) */}
      {libraryBrowserOpen && (
        <BlockLibraryBrowserDialog
          open
          systemExamples={systemBlockLibrary.examples}
          projectExamples={blockLibraryExamples.examples}
          strategyVariables={strategyVariables as Record<string, unknown>}
          onAddToCanvas={(exampleId, source) => {
            setLibraryBrowserOpen(false);
            void handleTemplateAddAtCenter(`example::${source}::${exampleId}`);
          }}
          onAdaptImmediately={(exampleId, source) => {
            setLibraryBrowserOpen(false);
            void handleTemplateAddAtCenter(`${EXAMPLE_SECONDARY_TEMPLATE_ID_PREFIX}${source}::${exampleId}`);
          }}
          onImport={async (systemExampleIds) => {
            try {
              const docs = systemBlockLibrary.examples
                .filter((example) => systemExampleIds.includes(example.id));
              const { BlockLibraryService } = await import('@modules/firebase-core/services/BlockLibraryService');
              const result = await importSystemExamples(
                docs,
                blockLibraryExamples.examples,
                new BlockLibraryService(projectId),
              );
              const skippedNote = result.skippedAlreadyImported.length > 0
                ? ` (${result.skippedAlreadyImported.length} already imported)`
                : '';
              setSyncNotice({
                message: `Imported ${result.imported.length} example${result.imported.length === 1 ? '' : 's'} to the project library${skippedNote}.`,
                severity: 'success',
              });
            } catch (err) {
              setSyncNotice({ message: `Import failed: ${(err as Error).message}`, severity: 'error' });
            }
          }}
          onClose={() => setLibraryBrowserOpen(false)}
        />
      )}

      {/* Block Examples Library: Save as example */}
      {saveAsExampleTarget && (
        <SaveBlockAsExampleDialog
          open
          blockName={saveAsExampleTarget.name}
          capture={async () => {
            const { StrategyExportService } = await import('@modules/strategy-blocks/services/StrategyExportService');
            return new StrategyExportService().buildBlockLibraryCapture(
              projectId,
              strategyId,
              saveAsExampleTarget.blockId,
            );
          }}
          save={async (input) => {
            const { BlockLibraryService } = await import('@modules/firebase-core/services/BlockLibraryService');
            await new BlockLibraryService(projectId).create(input);
          }}
          onClose={() => setSaveAsExampleTarget(null)}
          onSaved={() => setSyncNotice({ message: 'Block saved to the project library.', severity: 'success' })}
        />
      )}

      {applyThemeDialog && (
        <ApplyThemeGroupsDialog
          open={Boolean(applyThemeDialog)}
          onClose={() => setApplyThemeDialog(null)}
          theme={applyThemeDialog.theme}
          screens={applyThemeDialog.screens}
          sourceLabel={applyThemeDialog.sourceLabel}
          scopeLabel={applyThemeDialog.scopeLabel}
          initialGroups={applyThemeDialog.initialGroups}
          projectId={projectId}
          onConfirm={persistAppliedStrategyThemeScreens}
        />
      )}

      <StrategyThemeSelectionDialog
        open={themeReplaceOpen}
        onClose={() => {
          if (!themeReplaceLoading) setThemeReplaceOpen(false);
        }}
        title="Select Strategy Theme"
        options={themeReplaceOptions}
        selectedOption={selectedThemeReplaceOption}
        projectId={projectId}
        loading={themeReplaceLoading}
        error={themeReplaceError}
        onSelect={setSelectedThemeReplaceOption}
        onConfirm={handleConfirmReplaceTheme}
      />

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
    </AdaptingBlocksProvider>
  );
};
